/** SeamStitcher — the two things that happen at a chunk-mesh seam. */

import {
  GRID_SIZE, CHUNK_SIZE, MAX_PACKABLE_LOD, VOXEL_SCALE, voxelIndex,
  chunkKeyLod, parseChunkKey, packChunkKeyLod, unpackChunkKeyLod, type ChunkCoordLod,
} from '@worldify/shared';

/** The mesh grid: a chunk's own 32 voxels plus the MESH_MARGIN it borrows from its + neighbour. */
const GRID = GRID_SIZE;
import { ChunkGeometry } from './ChunkGeometry.js';
import { marginPolicyData, marginVoxelFrom } from './ChunkMesher.js';
import { LAYER_COUNT, TERRAIN_ATTRS, MATERIAL_SLOT_STRIDE, allocAttrArray, type AttrArray } from './LayerConfig.js';
import { restoreTriangles } from './indexRestore.js';
import {
  ALL_LAYERS, SkirtManager, faceAxis, faceIsHigh,
  type FinerCandidates, type RequiredSkirt,
} from './SkirtManager.js';
import {
  emitSeamFace, emptySeamStrip, newSeamCensus, RESOLVE_NO_CHUNK, RESOLVE_NO_VERTEX,
  SIDE_FINE, SRC_SHIFT, type SeamCensus, type SeamPatch, type SeamSource, type SeamStrip,
  type SeamTraceEvent,
} from './seamField.js';
import {
  digestFor, rimTable, bandCount, sideTris, BAND_DEPTH, BORDER_LOW_CELL, RIM_DEPTHS,
} from './seamDigest.js';

/** One emitter decision, placed in the world: the ledger the oracle joins to its open edges. */
export interface StitcherSeamEvent {
  owner: string;
  face: number;
  layer: number;
  ownerLod: number;
  fineLod: number;
  /** World position of the plane point `ev.c1`/`ev.c2` names. */
  wx: number;
  wy: number;
  wz: number;
  ev: SeamTraceEvent;
}

/** Face indices in the boundary CSR: [lowX, highX, lowY, highY, lowZ, highZ]. */
const FACE_LOW_X = 0, FACE_HIGH_X = 1, FACE_LOW_Y = 2, FACE_HIGH_Y = 3, FACE_LOW_Z = 4, FACE_HIGH_Z = 5;

/** The face id an OWNER-LEVEL skirt is published under. */
export const SKIRT_FACE = 6;

/**
 * THE MARGIN IS A BLOCK, NOT THREE SLABS — the four + offsets that are NOT faces.
 *
 * A mesh spans cells 0..GRID-2 in EVERY axis, so its + margin overhangs seven neighbours: three
 * faces, three edges and the +++ corner. `POSITIVE_MARGIN_OFFSETS_7` names all seven and the data
 * side of the system uses it everywhere — residency, remesh, the dependency contract. The MASK side
 * never has: `computeRequiredSkirts` walks six faces, so the other four overhangs are invisible to it.
 *
 * The chunk diagonally off a finer region's corner is the pure case. Every one of its six faces looks
 * at another coarse chunk, so it produces no RequiredSkirt at all and nothing of it is ever masked —
 * while its corner block, cells >= CHUNK_SIZE on two or three axes at once, sits squarely inside the
 * finer region and draws over it. Four coarse voxels deep, of which the outermost has no quads to
 * speak of (the last marched cell has no fourth cell for its outward faces), which is the two cells
 * of coarse surface standing on top of the skirt.
 *
 * These blocks are MASK-ONLY. There is no seam to build in them: they are wholly inside the finer
 * region, which holds that space natively, so the obligation is to stop drawing and nothing else.
 */
const DIAGONAL_MARGIN_OFFSETS: readonly (readonly [number, number, number])[] = [
  [1, 1, 0], [1, 0, 1], [0, 1, 1], [1, 1, 1],
];

/** How far past its + faces a coarse owner gives way, in ITS OWN cells. Exactly one ring — every
 *  triangle past the shared plane touches ring 32, so collapsing that band takes overlap to zero. */
const RING_MASK_CELLS = 1;

/** How far the world-global vertex resolver looks either side of a cell's own lod. The display onion
 *  is single-step, so two levels each way covers every displayed configuration. */
const RESOLVE_LOD_REACH = 2;

/** The answer for an owner that cannot have finer neighbours. Shared, so lod 0 allocates nothing. */
const EMPTY_CANDIDATES: FinerCandidates = { keys: [], lods: [] };

/** Where the per-face MISS half of a census row starts. */
const MISS_BASE = 6 * LAYER_COUNT;

/** `vertexMemo`'s cell packing: ±2^21 cells an axis (a lod-0 lattice at the ±32767 chunk bound is
 *  ±2^20), so the key stays an exact integer at 2^49 — a level/side field of 32 above the pair. */
const VMEMO_OFFSET = 0x200000;
const VMEMO_STRIDE = 0x400000;

/** A build with no filed read set — shared so the per-face signature allocates nothing for it. */
const EMPTY_READS: readonly number[] = [];

/** Grid-unit quantization: 1/16 voxel. Bit-identical seam positions round identically. */
const QUANT = 16;

// The skirt budget is OPT-IN: `flush(budgetEnd)` constrains the per-flush build loop, and the
// caller (VoxelWorld.update) supplies the shared display-pass clock. When no budgetEnd is passed

/** Per-direction seam descriptor: which face of THIS chunk meets which face of the */
interface SeamDir {
  dx: number; dy: number; dz: number;
  thisFace: number; nbrFace: number;
  positive: boolean;
}

const SEAM_DIRS: SeamDir[] = [
  { dx: 1, dy: 0, dz: 0, thisFace: FACE_HIGH_X, nbrFace: FACE_LOW_X, positive: true },
  { dx: -1, dy: 0, dz: 0, thisFace: FACE_LOW_X, nbrFace: FACE_HIGH_X, positive: false },
  { dx: 0, dy: 1, dz: 0, thisFace: FACE_HIGH_Y, nbrFace: FACE_LOW_Y, positive: true },
  { dx: 0, dy: -1, dz: 0, thisFace: FACE_LOW_Y, nbrFace: FACE_HIGH_Y, positive: false },
  { dx: 0, dy: 0, dz: 1, thisFace: FACE_HIGH_Z, nbrFace: FACE_LOW_Z, positive: true },
  { dx: 0, dy: 0, dz: -1, thisFace: FACE_LOW_Z, nbrFace: FACE_HIGH_Z, positive: false },
];

/**
 * ONE FACE'S VERTICES, GROUPED BY QUANTIZED IN-PLANE POSITION, in POOLED typed arrays.
 *
 * This was a `Map<number, number[]>` per side, per face, per layer — an array allocated for every
 * distinct position on the face, thirty-six of these maps per freshly-meshed chunk, on a loop with no
 * budget at all. The grouping is transient (the next reconcile overwrites it), so nothing needs
 * retaining; it just needs to stop allocating.
 *
 * Insertion order is preserved exactly — slots in first-seen key order, entries in scan order within a
 * slot — because the representative vertex of a position decides the normal that gets written, and a
 * different representative is a different mesh.
 */
interface FaceGroups {
  /** Quantized key -> slot. The one hash that stays: the key space is 20 bits, so a dense lattice
   *  would be a million slots to clear per face. */
  slotOf: Map<number, number>;
  /** Distinct keys, in first-seen order — the order the Map used to iterate in. */
  keys: Int32Array;
  /** Slot -> its first entry; entry -> the next entry in the same slot, or -1. */
  head: Int32Array;
  tail: Int32Array;
  next: Int32Array;
  /** Entry -> expanded vertex index. */
  vert: Int32Array;
  distinct: number;
}

/** Two sides are live at once, so two slots. reconcileSeam is synchronous and non-reentrant. */
const faceGroupPool: FaceGroups[] = [];

function faceGroups(slot: number, cap: number): FaceGroups {
  let g = faceGroupPool[slot];
  if (g === undefined) {
    g = {
      slotOf: new Map(), keys: new Int32Array(0), head: new Int32Array(0),
      tail: new Int32Array(0), next: new Int32Array(0), vert: new Int32Array(0), distinct: 0,
    };
    faceGroupPool[slot] = g;
  }
  if (g.vert.length < cap) {
    // Doubling, not exact: a face one vertex larger than the last must not reallocate five arrays.
    let n = g.vert.length || 256;
    while (n < cap) n *= 2;
    g.keys = new Int32Array(n);
    g.head = new Int32Array(n);
    g.tail = new Int32Array(n);
    g.next = new Int32Array(n);
    g.vert = new Int32Array(n);
  }
  g.slotOf.clear();
  g.distinct = 0;
  return g;
}

/** Pack two quantized in-plane grid coords (each < 1024) into one integer key. */
function packKey(qa: number, qb: number): number {
  return (qa & 0x3ff) | ((qb & 0x3ff) << 10);
}


/**
 * A renderable apron: world-space positions plus a full set of terrain attributes copied from the
 * source vertices, so the consumer only has to make a BufferGeometry out of it.
 */
export interface ApronMesh {
  positions: Float32Array;
  indices: Uint32Array;
  /** Every TERRAIN_ATTRS entry except position, keyed by attribute name, in that attribute's byte
   *  format (vertexFormat.ts); plus skirtKind. */
  attrs: Record<string, AttrArray>;
}

export class SeamStitcher {
  private pending = new Set<string>();
  /** Chunks whose CROSS-LOD seams need re-evaluating (their own remesh, or a coarse neighbour's). */
  private crossPending = new Set<string>();

  /**
   * CHUNKS WHOSE DISPLAY FLIPPED SINCE THE LAST FLUSH — the owners they implicate must be built in
   * THIS commit, not deferred.
   *
   * The commit already applies the flips and then flushes seams in the same call, and a strip goes
   * straight to the scene rather than through the merge, so a skirt built here is on screen this
   * frame. What made them late was the budget: the owner loop sorted unresolved-first then by
   * distance, with no notion of "this owner is the consequence of a flip that just happened", so the
   * one owner that had to build now could be parked behind unrelated debt and the newly shown chunk
   * drew with a hole until a later frame.
   *
   * Bounding this is what keeps it from thrashing: the set is the flips of ONE commit, and
   * DisplayCommit already caps those. Flips also arrive grouped into parent<->octet transactions that
   * land whole, so an octet's eight children implicate their owners once between them rather than
   * eight times.
   */
  private flipped = new Set<string>();

  /** Toggle (debug): when false, flush() does nothing — for A/B comparison. */
  enabled = true;

  constructor(
    private readonly geometries: Map<string, ChunkGeometry>,
    private readonly markGroupDirty: (chunkKey: string) => void,
    /** Is this chunk actually DRAWN this frame? Ownership must follow what is on screen, not what */
    private readonly isDisplayed: (chunkKey: string) => boolean = () => true,
    /** Where a built strip goes. Called with a strip for a (chunk, face) that needs one, or null when */
    private readonly onApron: (
      key: string, face: number, layer: number, apron: ApronMesh | null,
    ) => void = () => {},
    /** A chunk's INDEX buffer was rewritten in place — the band mask collapsing triangles, or its */
    private readonly onIndicesChanged: (chunkKey: string) => void = () => {},
    /** Where an OWNER-LEVEL skirt goes: one strip per (coarse owner, layer), or null to drop it. */
    private readonly onSkirt: (
      ownerKey: string, layer: number, skirt: ApronMesh | null,
    ) => void = (ownerKey, layer, skirt) => this.onApron(ownerKey, SKIRT_FACE, layer, skirt),
    /** VOXEL DATA BY COORDS, at any lod — the seam's sign source. Case L reads the fine chunk's +a
     *  same-LOD neighbour, which is resident (the sacrificial ring) but has no geometry, so the
     *  `geometries` map cannot reach it. Defaults to whatever geometry there is. */
    private readonly getChunkAt: (
      cx: number, cy: number, cz: number, lod: number,
    ) => { readonly data: Uint32Array } | undefined | null =
      (cx, cy, cz, lod) => this.geometries.get(chunkKeyLod(cx, cy, cz, lod))?.chunk,
    /** GEOMETRY AND DISPLAY BY PACKED KEY (packChunkKeyLod) — the resolve path's two lookups, which
     *  used to build a key string per attempt (measured: the largest string allocator in a flush).
     *  The defaults go through the string maps, for tests. */
    private readonly geometryByPacked: (packed: number) => ChunkGeometry | undefined =
      (packed) => {
        const c = unpackChunkKeyLod(packed);
        return this.geometries.get(chunkKeyLod(c.cx, c.cy, c.cz, c.lod));
      },
    private readonly isDisplayedPacked: (packed: number) => boolean =
      (packed) => {
        const c = unpackChunkKeyLod(packed);
        return this.isDisplayed(chunkKeyLod(c.cx, c.cy, c.cz, c.lod));
      },
  ) {
    // Clear caches on construction (important for tests that reuse the stitcher).
    this.finerCache.clear();
    this.requiredByOwner.clear();
  }

  /**
   * ONE PUBLISH PER CHUNK PER FLUSH. The same-LOD pass touches a chunk once per neighbour and the
   * masks once per owner it participates in, and every publish is a copy into the batch — so inside
   * a flush the edits are collected and published together at its end. Outside a flush (an eviction's
   * retraction) they publish at once.
   */
  private flushing = false;
  private readonly normalsDirty = new Set<string>();
  private readonly indicesDirty = new Set<string>();

  /** Record an in-place INDEX rewrite: bump the geometry's index clock and tell the consumer. */
  private notifyIndicesChanged(key: string, geo: ChunkGeometry): void {
    geo.indexEpoch = (geo.indexEpoch ?? 0) + 1;
    if (this.flushing) this.indicesDirty.add(key); else this.onIndicesChanged(key);
  }

  private publishDeferred(): void {
    this.flushing = false;
    for (const key of this.normalsDirty) this.markGroupDirty(key);
    this.normalsDirty.clear();
    for (const key of this.indicesDirty) this.onIndicesChanged(key);
    this.indicesDirty.clear();
  }


  /** Which LAYERS each coarse owner currently has a skirt published for. */
  private readonly skirtLayers = new Map<string, Set<number>>();
  /** What each owner's skirts were built FROM — owner mesh clocks plus every participant's, so a quiet */
  private readonly skirtSig = new Map<string, string>();

  /** Per owner, the keys whose DISPLAY VERDICT its last build rested on — see `trackRead`. */
  private readonly skirtReads = new Map<string, number[]>();
  /** ...and the reverse: the owners to bring back when one of those keys moves. */
  private readonly readBy = new Map<number, Set<string>>();
  private reading: Set<number> | null = null;
  private readingSkip: Set<number> | null = null;

  /**
   * Memoized finerNeighboursOf results, OWNER-MAJOR with six faces per entry: a hit costs an array
   * index, where the `${packed},${face}` key built a string on every lookup, hits included, and this
   * is read six times per candidate owner. The result is positional — it changes only when a chunk is
   * added or removed, or a LOD steps.
   */
  private readonly finerCache = new Map<number, (FinerCandidates | undefined)[]>();

  /** Invalidate finerNeighboursOf cache entries for a given chunk. */
  private invalidateFinerCache(key: string): void {
    const { cx, cy, cz, lod } = this.coordsOf(key);
    if (lod === 0) return; // lod-0 can never be a coarser owner
    this.finerCache.delete(packChunkKeyLod(cx, cy, cz, lod));
  }

  // ---- Required skirts cache ----

  /**
   * WHAT EACH COARSE OWNER REQUIRES, PER-OWNER, keyed by the packed owner key. One owner's records read only its own display state
   * and lod plus its finer-neighbour candidates' (see SkirtManager.computeOwnerSkirts), so a change
   * reaches a bounded handful of owners — not all of them.
   *
   * The single global cache this replaces was discarded outright by ANY chunk's change, and one of
   * the three things that calls that is a mesh apply. Applies land every frame while the player
   * moves, so the cache was invalid on every flush by construction and computeRequiredSkirts
   * re-enumerated the entire candidate set each time (measured: `compute=N skipped=0` in every
   * window). The bookkeeping for a finer answer was already here and simply unused.
   */
  private readonly requiredByOwner = new Map<number, RequiredSkirt[]>();

  /**
   * A chunk's geometry or display state changed: drop the memo for every owner whose answer could
   * mention it. THE ONE INVALIDATION FUNNEL — onGeometryChanged, onDisplayChanged and onRemoved all
   * arrive here, which is why a per-owner memo is safe to keep at all.
   *
   * The affected set is derivable arithmetically rather than by search. `computeFinerNeighbours`
   * reaches fineLod in [lod-2, lod-1] across a FACE, so a chunk at lod L can only be a participant
   * of an owner at L+1 or L+2 whose position is this chunk's ancestor stepped ONE cell along a face
   * axis — and the chunk itself may be an owner. Thirteen deletes, and a superset of the exact set
   * (some of those positions will not list it), which is the safe direction to err in.
   */
  private invalidateRequiredFor(key: string): void {
    const { cx, cy, cz, lod } = this.coordsOf(key);
    this.requiredByOwner.delete(packChunkKeyLod(cx, cy, cz, lod));
    for (let up = 1; up <= 2; up++) {
      const ownerLod = lod + up;
      if (ownerLod > MAX_PACKABLE_LOD) break;
      const ax = cx >> up, ay = cy >> up, az = cz >> up;
      for (const dir of SEAM_DIRS) {
        this.requiredByOwner.delete(
          packChunkKeyLod(ax + dir.dx, ay + dir.dy, az + dir.dz, ownerLod),
        );
      }
    }
  }

  /** Per-pass memos for `registered`/`drawable` — see reconcileCoarseSkirts. Cleared on entry; kept
   *  as fields only so a flush does not allocate two maps. */
  private readonly regMemo = new Map<string, boolean>();
  private readonly drawMemo = new Map<string, boolean>();
  private readonly drawMemoP = new Map<number, boolean>();

  /**
   * PARSED KEYS, MEMOIZED FOR THE FLUSH. `parseChunkKey` splits the string, allocates four substrings
   * and a result object — and this file asks it about the same key five to ten times per owner
   * rebuild: the owner in every stage, each participant in the cover, the fine mask and the flank.
   *
   * There is nothing to invalidate. `parseChunkKey` is a pure function of its string, so the memo can
   * never be stale; it is cleared each flush only to bound what it holds to one frame's keys.
   *
   * The returned object is SHARED. Every site destructures it or copies out of it, and none may start
   * mutating it.
   */
  private readonly coordMemo = new Map<string, ChunkCoordLod>();

  private coordsOf(key: string): ChunkCoordLod {
    let got = this.coordMemo.get(key);
    if (got === undefined) {
      got = parseChunkKey(key);
      this.coordMemo.set(key, got);
    }
    return got;
  }

  // ---- Distance cache for budget ordering ----

  /**
   * Cached squared distance from each chunk to the player centre.
   * Used for distance-ordering skirt processing within the budget.
   */
  private readonly distSqCache = new Map<number, number>();
  private playerCx = 0;
  private playerCy = 0;
  private playerCz = 0;

  /**
   * Set the player chunk centre for distance-based ordering.
   * Called by VoxelWorld before each flush.
   */
  setPlayerCenter(cx: number, cy: number, cz: number): void {
    this.playerCx = cx;
    this.playerCy = cy;
    this.playerCz = cz;
  }

  /** Squared distance from a chunk to the player centre, in lod-0 chunk units. */
  private distanceSqToCenter(key: string): number {
    const { cx, cy, cz, lod } = this.coordsOf(key);
    const packed = packChunkKeyLod(cx, cy, cz, lod);
    let d = this.distSqCache.get(packed);
    if (d === undefined) {
      // Scale chunk coords to lod-0 units for consistent distance ordering across LODs.
      const scale = 1 << lod;
      const dx = (cx + 0.5) * scale - (this.playerCx + 0.5);
      const dy = (cy + 0.5) * scale - (this.playerCy + 0.5);
      const dz = (cz + 0.5) * scale - (this.playerCz + 0.5);
      d = dx * dx + dy * dy + dz * dz;
      this.distSqCache.set(packed, d);
    }
    return d;
  }
  /** Per-flush skirt gauges. */
  lastSkirts = 0;
  lastSkirtTris = 0;
  lastSkirtOwners = 0;
  lastSkirtMasked = 0;
  /** Owners deferred due to budget — not errors, just caught by the throttle. */
  lastSkirtDeferred = 0;
  /** ...of which were built UNCONDITIONALLY because a flip this commit made them responsible. The
   *  per-frame cost of the no-gap guarantee, and the number to watch if a swap near the player
   *  spikes the frame. */
  lastSkirtPriority = 0;
  /** The flush's owner build time in total, and its single most expensive owner: is a spike many
   *  owners or one? */
  lastOwnerMs = 0;
  lastOwnerMaxMs = 0;
  lastOwnerMaxKey = '';
  /** OWNERS whose requirement was recomputed this flush, and owners served from the per-owner memo.
   *  Counts, not the flags they were while the cache was all-or-nothing: the question is no longer
   *  "did it run" but "how much of the candidate set had to". A healthy moving frame recomputes the
   *  handful of owners this frame's changes reached and skips the rest. */
  lastSkirtComputeRan = 0;
  lastSkirtComputeSkipped = 0;
  /** Face×layer emitter runs this flush, and the ones the empty-face cache answered (see faceEmpty).
   *  The emitter is the per-owner cost, so this pair is the one that says whether a spike was work. */
  lastSkirtFacesEmitted = 0;
  lastSkirtFacesSkipped = 0;

  /** WHY THE FIELD EMITTER DID OR DID NOT PUT A FACE AT EACH CROSSING, this flush. Every skip is a
   *  named reason (spec §7), so a hole is a count rather than a squint at a hillside. */
  readonly seamCensus: SeamCensus = newSeamCensus();

  /** The census as a one-line gauge string, for the F3 panel. */
  seamCensusSummary(): string {
    const c = this.seamCensus;
    return `x${c.crossings} t${c.triangles} f${c.fans} q${c.quads} b${c.bandFaces}`
      + ` hid${c.hidden + c.bandHidden} noPlane${c.noPlaneData} noFine${c.noFineVertex}`
      + ` noApex${c.noCoarseApex} noChunk${c.noChunk}`
      + ` band-${c.bandNoVertex}/${c.bandNoData}`
      + ` rim${c.rimEdges} unzip${c.rimUnzipped}`
      + ` cap${c.capFans}/${c.capNoFine}`
      + ` lid${c.lidCoarse}/${c.lidNoVertex}/${c.lidHidden}/${c.lidNoData}`;
  }


  /** THE RETRY SET (C4 — "a skirt that missed is a consequence still owed"). OWNER keys with a */
  private readonly unresolved = new Set<string>();

  /** Read-only view of the retry set — coarse OWNER keys. */
  get unresolvedKeys(): ReadonlySet<string> { return this.unresolved; }

  /** Coarse owners that currently HOLD a skirt, and how many the displayed configuration REQUIRES — */
  get skirtOwnersBuilt(): number { return this.skirtLayers.size; }
  get skirtOwnersRequired(): number { return this.bridgedOwners.size; }

  /** Read-only: the layers this owner holds a built strip for. Measurement only. */
  debugSkirtLayers(owner: string): ReadonlySet<number> | undefined { return this.skirtLayers.get(owner); }

  /** Measurement only: per owner, `face * LAYER_COUNT + layer` → triangles the last build put on that
   *  face. -1 = not bridged, -2 = no surface on the FINE side, -3 = nothing drawable at the plane,
   *  -4 = drew nothing, and every refusal was a coarse apex the contour does not hold. */
  private readonly skirtFaceTris = new Map<string, Int32Array>();

  /**
   * ONE RESOLVE PER LATTICE VERTEX PER FACE. Every in-plane edge resolves both of its corners, and a
   * lattice point is a corner of four edges — plus the band and lid passes ask for the same points
   * again — so `seamVertexAt` ran four to six times for one answer, each run a five-level, four-alternate
   * probe. The coarse side already had `resolveApex`'s memo; this is the fine side's, and it covers the
   * misses too (the miss path is the expensive one: it exhausts every level before giving up).
   *
   * Keyed (level, side, global cell) — NOT the patch, because two patches of one level agree about a
   * shared cell by construction. Cleared per face: the answers depend on `sources`, which is per strip.
   */
  private readonly vertexMemo = new Map<number, number>();

  /**
   * FACE×LAYER COMBINATIONS THAT EMITTED NOTHING, with the inputs they were empty under — owner ->
   * slot (`face * LAYER_COUNT + layer`) -> signature plus the audit counts that face reported.
   * Dropped with the owner's strips, so a re-meshed owner re-derives them.
   */
  private readonly faceEmpty = new Map<string, Map<number, { sig: string; tris: number; miss: number }>>();

  /** Read-only: the owners that census holds a row for. Measurement only. */
  get debugSkirtFaceOwners(): Iterable<string> { return this.skirtFaceTris.keys(); }

  /** Read-only per-(owner, face, layer) seam coverage — see `skirtFaceTris` for the encoding. */
  debugSkirtFace(owner: string, face: number, layer: number): number {
    const row = this.skirtFaceTris.get(owner);
    return row ? row[face * LAYER_COUNT + layer] : -1;
  }

  /** Read-only: ROWS that face's last build refused for want of a drawn source. */
  debugSkirtFaceMiss(owner: string, face: number, layer: number): number {
    const row = this.skirtFaceTris.get(owner);
    return row ? row[MISS_BASE + face * LAYER_COUNT + layer] : 0;
  }

  /** Owners with something to bridge — a candidate whose participants draw nothing owes nothing. */
  private readonly bridgedOwners = new Set<string>();


  /** Triangles a COARSE OWNER collapsed to get out of its finer neighbours' way, per chunk, per layer. */
  private readonly bands = new Map<string, Map<number, number[]>>();
  /** The DIAGONAL block masks, kept apart from `bands` so they cannot disturb the face path: they
   *  answer to a different question and have their own lifecycle. See DIAGONAL_MARGIN_OFFSETS. */
  private readonly diagBands = new Map<string, Map<number, number[]>>();
  /** Which diagonal finer neighbours a block mask was computed against, so a steady state is inert. */
  private readonly diagSig = new Map<string, string>();

  /**
   * THE CASE-L PARTICIPANT MASKS (§4): owner -> fine chunk -> layer -> collapsed triangles. A fine
   * chunk below a coarse owner overhangs it, so IT is the low side of the plane and gives up its
   * cells >= CHUNK_SIZE band. Part of the owner's transaction — one fine face has exactly one owner,
   * so there is nothing to refcount.
   */
  private readonly fineBands = new Map<string, Map<string, Map<number, number[]>>>();
  /** Reverse index: fine chunk -> the owners holding a mask on it, for remesh invalidation. */
  private readonly fineMaskOwners = new Map<string, Set<string>>();

  /** Put ONE layer's collapsed triangles back. */
  private restoreLayerBand(geo: ChunkGeometry, layer: number, tris: readonly number[]): boolean {
    const g = geo.getLayerGeometry(layer);
    if (!g) return false;
    // From the pristine snapshot (indexRestore): the collapse is destructive and the index buffer is
    // a real one, so the original triple of triangle t is NOT 3t..3t+2.
    if (!restoreTriangles(g, tris)) return false;
    geo.markIndicesChanged(layer);
    return true;
  }

  /** Put every triangle this chunk collapsed back, exactly. */
  private restoreBands(key: string): boolean {
    const per = this.bands.get(key);
    if (!per) return false;
    this.bands.delete(key);
    const geo = this.geometries.get(key);
    let restored = false;
    for (const [layer, tris] of per) {
      const g = geo?.getLayerGeometry(layer);
      if (!g || !restoreTriangles(g, tris)) continue;   // from the pristine snapshot (indexRestore)
      geo!.markIndicesChanged(layer);
      restored = true;
    }
    return restored;
  }

  /** Collapse one layer's triangles within `cells` of a face, under `foot` only (§4). */
  private maskBand(geo: ChunkGeometry, layer: number, axis: number, low: boolean, cells: number,
    into: number[], foot?: readonly number[]): void {
    const g = geo.getLayerGeometry(layer);
    const cellIdx = geo.getCellIndices(layer);
    const idx = g?.index?.array as Uint32Array | undefined;
    if (!g || !cellIdx || !idx) return;
    const digest = digestFor(geo, layer);
    if (!digest) return;
    const comps = digest.comps;
    const t1 = (axis + 1) % 3, t2 = (axis + 2) % 3;
    // One cell of slack: the seam's own scan reaches one cell past a patch where no patch follows.
    const inFoot = (v: number): boolean => {
      if (!foot || foot.length === 0) return true;
      const c1 = comps[v * 3 + t1], c2 = comps[v * 3 + t2];
      for (let i = 0; i < foot.length; i += 3) {
        const n = foot[i + 2];
        if (c1 >= foot[i] - 1 && c1 <= foot[i] + n && c2 >= foot[i + 1] - 1 && c2 <= foot[i + 1] + n) {
          return true;
        }
      }
      return false;
    };
    const inBand = (v: number): boolean => {
      const c = comps[v * 3 + axis];
      // From the SHARED RING outward, anchored to CHUNK_SIZE rather than to GRID: the plane does not
      // move when margin voxels are added beyond it.
      return (low ? c < cells : c > CHUNK_SIZE - cells) && inFoot(v);
    };
    // The collapse is destructive — idx[a+1] = idx[a] loses the triangle — so the outline has to be
    // taken BEFORE, not recovered after.
    // ...and only for the F6 overlay: with it off, neither the world position nor the closure exists.
    const pos = this.seamDebugOn
      ? (g.getAttribute('position')?.array as ArrayLike<number> | undefined) : undefined;
    const wp = pos ? geo.chunk.getWorldPosition() : null;
    const edge = pos && wp ? (u: number, v: number): void => {
      if (this.seamDebug.masked.length > 90000) return;
      this.seamDebug.masked.push(
        pos[u * 3] + wp.x, pos[u * 3 + 1] + wp.y, pos[u * 3 + 2] + wp.z,
        pos[v * 3] + wp.x, pos[v * 3 + 1] + wp.y, pos[v * 3 + 2] + wp.z);
    } : null;
    // ONLY THE TRIANGLES THAT CAN BE IN THE BAND. The high band's deepest cell is GRID-1-cells and the
    // digest carries GRID-BAND_DEPTH inward, so a wider mask falls back to the whole buffer rather
    // than silently missing the triangles past the digest's reach.
    const triCount = idx.length / 3;
    const band = cells < BAND_DEPTH ? sideTris(digest, axis, low) : null;
    const walk = band ? band.length : triCount;
    for (let wi = 0; wi < walk; wi++) {
      const t = band ? band[wi] : wi;
      if (t >= triCount) continue;
      const a = t * 3;
      if (idx[a] === idx[a + 1]) continue;                    // already collapsed
      // TOUCHING THE BAND IS ENOUGH, and the whole point is that the border then ENDS WHERE THE
      // MASK SAYS IT ENDS.
      if (!inBand(idx[a]) && !inBand(idx[a + 1]) && !inBand(idx[a + 2])) continue;
      if (edge) {
        edge(idx[a], idx[a + 1]);
        edge(idx[a + 1], idx[a + 2]);
        edge(idx[a + 2], idx[a]);
      }
      idx[a + 1] = idx[a];
      idx[a + 2] = idx[a];
      into.push(t);
    }
    if (into.length && this.seamDebugOn) this.seamDebug.version++;
    if (into.length) geo.markIndicesChanged(layer);
  }

  /**
   * ONE CHUNK'S DIAGONAL MARGIN BLOCKS THAT SOMEBODY ELSE DRAWS AT A DIFFERENT LOD (§4).
   *
   * Symmetric in the level step. A coarse chunk's block can sit over a displayed FINER region (the
   * original case); a FINE chunk's block can sit over a displayed COARSER one, which is the corner
   * of a fine region overhanging its coarse surround. Same-LOD-displayed volume is never masked —
   * the ordinary duplicated ring stays, as ever.
   */
  private diagonalBlocks(key: string): { axes: number[]; keys: string[]; finer: boolean }[] {
    const { cx, cy, cz, lod } = this.coordsOf(key);
    const out: { axes: number[]; keys: string[]; finer: boolean }[] = [];
    for (const d of DIAGONAL_MARGIN_OFFSETS) {
      const nx = cx + d[0], ny = cy + d[1], nz = cz + d[2];
      if (this.drawnP(packChunkKeyLod(nx, ny, nz, lod))) continue;
      const axes: number[] = [];
      for (let a = 0; a < 3; a++) if (d[a] === 1) axes.push(a);
      const keys: string[] = [];
      if (lod > 0) {
        const cand = this.computeFinerDiagonal(cx, cy, cz, lod, d);
        for (let i = 0; i < cand.keys.length; i++) {
          const k = cand.keys[i];
          if (cand.lods[i] >= lod || keys.includes(k) || !this.drawn(k)) continue;
          keys.push(k);
        }
      }
      if (keys.length > 0) { out.push({ axes, keys, finer: true }); continue; }
      for (let up = 1; up <= 2 && lod + up <= MAX_PACKABLE_LOD; up++) {
        if (!this.drawnP(packChunkKeyLod(nx >> up, ny >> up, nz >> up, lod + up))) continue;
        out.push({ axes, keys: [chunkKeyLod(nx >> up, ny >> up, nz >> up, lod + up)], finer: false });
        break;
      }
    }
    return out;
  }

  /** Displayed AND meshed — the only state that counts as somebody drawing that volume. */
  private drawn(key: string): boolean {
    return this.drawnP(this.packedOf(key));
  }

  /** `drawn` by packed key — the hot paths hold coordinates, not strings. */
  private drawnP(packed: number): boolean {
    this.trackRead(packed);
    return !!this.geometryByPacked(packed)?.hasGeometry() && this.isDisplayedPacked(packed);
  }

  private packedOf(key: string): number {
    const c = this.coordsOf(key);
    return packChunkKeyLod(c.cx, c.cy, c.cz, c.lod);
  }

  /**
   * The candidates under ONE diagonal offset. The axes the margin crosses land on the neighbour's
   * first cell; the axes it does not sweep this chunk's own span, exactly as a face does in its two
   * tangential directions.
   */
  private computeFinerDiagonal(
    cx: number, cy: number, cz: number, lod: number, d: readonly [number, number, number],
  ): FinerCandidates {
    const base = [cx, cy, cz];
    const p = [0, 0, 0];
    const keys: string[] = [];
    const lods: number[] = [];
    for (let fineLod = lod - 1; fineLod >= Math.max(0, lod - 2); fineLod--) {
      const n = 1 << (lod - fineLod);
      const free: number[] = [];
      for (let a = 0; a < 3; a++) {
        if (d[a] === 1) p[a] = base[a] * n + n; else free.push(a);
      }
      const sweep = (i: number): void => {
        if (i === free.length) {
          keys.push(chunkKeyLod(p[0], p[1], p[2], fineLod));
          lods.push(fineLod);
          return;
        }
        const a = free[i];
        for (let j = 0; j < n; j++) { p[a] = base[a] * n + j; sweep(i + 1); }
      };
      sweep(0);
    }
    return { keys, lods };
  }

  /** Collapse every triangle of one layer with a corner inside the block `axes` names, and record it. */
  private maskBlock(
    geo: ChunkGeometry, layer: number, axes: readonly number[], into: number[],
  ): void {
    const g = geo.getLayerGeometry(layer);
    const cellIdx = geo.getCellIndices(layer);
    const idx = g?.index?.array as Uint32Array | undefined;
    if (!g || !cellIdx || !idx) return;
    const digest = digestFor(geo, layer);
    if (!digest) return;
    const comps = digest.comps;
    const inBlock = (v: number): boolean => {
      for (let i = 0; i < axes.length; i++) if (comps[v * 3 + axes[i]] < CHUNK_SIZE) return false;
      return true;
    };
    // Every triangle in the block has a corner past the plane on axes[0], so that side's border
    // triangles are a superset of what this can collapse — a band walk rather than the whole buffer.
    const band = sideTris(digest, axes[0], false);
    const triCount = idx.length / 3;
    for (let wi = 0; wi < band.length; wi++) {
      const t = band[wi];
      if (t >= triCount) continue;
      const a = t * 3;
      if (idx[a] === idx[a + 1]) continue;                    // already collapsed
      if (!inBlock(idx[a]) && !inBlock(idx[a + 1]) && !inBlock(idx[a + 2])) continue;
      idx[a + 1] = idx[a];
      idx[a + 2] = idx[a];
      into.push(t);
    }
    if (into.length) geo.markIndicesChanged(layer);
  }

  /**
   * MASK THIS OWNER'S DIAGONAL MARGIN BLOCKS, if a finer region is under them.
   *
   * Inert once applied: the signature is the diagonal finer set, so a steady frame does nothing. No
   * per-cell cover test, unlike the face bands — a face band runs the length of a face and parts of
   * it can lie outside the finer region entirely, whereas one of these blocks is wholly inside the
   * diagonal neighbour's footprint by construction. What it does check is that the finer side has a
   * surface at the boundary at all, so an empty layer is never masked into a hole.
   */
  private maskDiagonalMargin(owner: string): void {
    if (!this.enabled) return;
    const geo = this.geometries.get(owner);
    if (!geo?.hasGeometry()) return;
    const blocks = this.diagonalBlocks(owner);
    const sig = blocks.map((b) => `${b.axes.join('')}>${b.keys.join(',')}`).join('|');
    if (this.diagSig.get(owner) === sig) return;
    if (this.restoreDiagBands(owner)) this.notifyIndicesChanged(owner, geo);
    this.diagSig.set(owner, sig);
    if (blocks.length === 0) return;
    const per = new Map<number, number[]>();
    for (const layer of ALL_LAYERS) {
      const list: number[] = [];
      for (const b of blocks) {
        let backed = false;
        for (const k of b.keys) {
          const og = this.geometries.get(k);
          if (!og) continue;
          // A finer block sits inside the other chunk's cell-0 layer; a coarser one is somewhere in
          // its interior, where the only cheap question is whether it draws this layer at all.
          if (b.finer ? this.hasBorderSurface(og, layer, b.axes[0], 0) : !!og.getCellIndices(layer)) {
            backed = true; break;
          }
        }
        if (backed) this.maskBlock(geo, layer, b.axes, list);
      }
      if (list.length) per.set(layer, list);
    }
    if (per.size === 0) return;
    this.diagBands.set(owner, per);
    this.notifyIndicesChanged(owner, geo);
  }

  /** Put a diagonal block mask back — the ids are into the buffer that still holds them. */
  private restoreDiagBands(key: string): boolean {
    this.diagSig.delete(key);
    const per = this.diagBands.get(key);
    if (!per) return false;
    this.diagBands.delete(key);
    const geo = this.geometries.get(key);
    let restored = false;
    for (const [layer, tris] of per) {
      const g = geo?.getLayerGeometry(layer);
      if (!g || !restoreTriangles(g, tris)) continue;
      geo!.markIndicesChanged(layer);
      restored = true;
    }
    return restored;
  }

  /**
   * THE CASE-L PARTICIPANT MASK: one fine chunk gives up its cells >= CHUNK_SIZE band on `axis`.
   *
   * The fine chunk sits BELOW the plane and overhangs the owner, so §4 makes it the low side: it
   * yields the plane band (which the seam replaces at the same fine resolution) and the overhang
   * (which the owner's cells 0-1 now draw natively, unmasked). Recorded against the owner, restored
   * with the owner's transaction.
   */
  private maskParticipant(
    owner: string, fine: string, layer: number, axis: number,
    into: Map<string, Map<number, number[]>>,
  ): void {
    const fineGeo = this.geometries.get(fine);
    if (!fineGeo) return;
    const list: number[] = [];
    this.maskBand(fineGeo, layer, axis, false, RING_MASK_CELLS, list);
    if (list.length === 0) return;
    let per = into.get(fine);
    if (!per) { per = new Map(); into.set(fine, per); }
    const had = per.get(layer);
    if (had) had.push(...list); else per.set(layer, list);
    let owners = this.fineMaskOwners.get(fine);
    if (!owners) { owners = new Set(); this.fineMaskOwners.set(fine, owners); }
    owners.add(owner);
  }

  /** Commit one owner's participant masks and publish each touched fine chunk's index edit once. */
  private commitParticipantMasks(
    owner: string, record: Map<string, Map<number, number[]>>,
  ): void {
    if (record.size === 0) { this.fineBands.delete(owner); return; }
    this.fineBands.set(owner, record);
    for (const [fine, per] of record) {
      const geo = this.geometries.get(fine);
      if (!geo) continue;
      let n = 0;
      for (const tris of per.values()) n += tris.length;
      if (n === 0) continue;
      this.lastSkirtMasked += n;
      this.notifyIndicesChanged(fine, geo);
    }
  }

  /** Drop every participant mask this owner holds, restoring the fine index buffers. */
  private restoreOwnerFineMasks(owner: string): void {
    const record = this.fineBands.get(owner);
    if (!record) return;
    this.fineBands.delete(owner);
    for (const [fine, per] of record) {
      const owners = this.fineMaskOwners.get(fine);
      if (owners) { owners.delete(owner); if (owners.size === 0) this.fineMaskOwners.delete(fine); }
      const geo = this.geometries.get(fine);
      if (!geo) continue;
      let restored = false;
      for (const [layer, tris] of per) if (this.restoreLayerBand(geo, layer, tris)) restored = true;
      if (restored) this.notifyIndicesChanged(fine, geo);
    }
  }

  /**
   * FORGET (never restore) every participant mask on `fine` — its index buffer has been REPLACED by
   * a remesh, so the recorded ids point into a dead array. The owners' signatures die with them so
   * the next flush re-derives the masks against the new buffer.
   */
  private invalidateFineMasksFor(fine: string): void {
    const owners = this.fineMaskOwners.get(fine);
    if (!owners) return;
    this.fineMaskOwners.delete(fine);
    for (const owner of owners) {
      this.fineBands.get(owner)?.delete(fine);
      this.skirtSig.delete(owner);
    }
  }


  /** Queue a freshly-meshed chunk for seam reconciliation on the next flush. */
  enqueue(key: string): void {
    this.pending.add(key);
  }

  /** THE F5 A/B: skirts and their masks entirely on or entirely off, live. */
  /** THE F6 OVERLAY BUFFER. World-space throughout — the chains are already world, and the raw vertex */
  readonly seamDebug = {
    /** EVERY TRIANGLE THE MASK COLLAPSED, as its three edges. White. The mask is the only thing in
     *  the pipeline that DELETES geometry, so it is the only way to get a hole without the seam being
     *  involved. If a gap coincides with white, the mask removed it and nothing replaced it; if it
     *  does not, the geometry was never built. */
    masked: [] as number[],
    /** Kept so the overlay's readers stay typed; the strip machinery that fed them is gone. */
    pts: [] as number[], rejected: [] as number[], segs: [] as number[],
    coarse: [] as number[], rimLow: [] as number[], open: [] as number[],
    version: 0,
  };

  private seamDebugOn = false;

  /** THE SEAM LEDGER (docs §9). Opt-in and off in the app: set by the headless oracle only, so
   *  every emitted triangle and every refusal can be joined to a measured open edge by position. */
  onSeamEvent: ((e: StitcherSeamEvent) => void) | null = null;

  setSeamDebug(on: boolean): void {
    if (on === this.seamDebugOn) return;
    this.seamDebugOn = on;
    this.seamDebug.pts.length = 0;
    this.seamDebug.rejected.length = 0;
    this.seamDebug.segs.length = 0;
    this.seamDebug.open.length = 0;
    this.seamDebug.coarse.length = 0;
    this.seamDebug.masked.length = 0;
    this.seamDebug.rimLow.length = 0;
    this.seamDebug.version++;
    if (!on) return;
    this.skirtSig.clear();
    for (const key of this.geometries.keys()) this.enqueue(key);
  }



  setSkirtsEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    if (!on) {
      for (const key of [...this.skirtLayers.keys()]) this.dropSkirts(key);
      // ...and the participant masks of owners that publish no strip: they live on OTHER chunks'
      // index buffers, so the loop above cannot reach them.
      for (const key of [...this.fineBands.keys()]) this.restoreOwnerFineMasks(key);
      // A diagonal-only owner owes no skirt, so it is not in `skirtLayers` and the loop above misses
      // it entirely. Its block still has to come back, or F5 stops being an A/B.
      for (const key of [...this.diagBands.keys()]) {
        if (!this.restoreDiagBands(key)) continue;
        const g = this.geometries.get(key);
        if (g) this.notifyIndicesChanged(key, g);
      }
      this.diagSig.clear();
      this.skirtSig.clear();
      this.unresolved.clear();
      this.pending.clear();
      this.crossPending.clear();
      return;
    }
    this.skirtSig.clear();
    for (const key of this.geometries.keys()) this.enqueue(key);
  }

  /** THE symmetric trigger (M7). A seam verdict depends on its NEIGHBOURS' geometry and display */
  onGeometryChanged(key: string): void {
    // THE SKIRT DIES WITH THE MESH IT DESCRIBES. This fires from exactly one production site — the
    // remesh-apply listener — so it means the MESHER replaced this chunk's geometry, and the strip
    this.dropSkirts(key);
    // The diagonal block mask is the same shape and the same hazard: ids into the OLD buffer. Dropped,
    // never restored, and re-derived on the next flush because its signature goes with it.
    this.diagBands.delete(key);
    this.diagSig.delete(key);
    // The FINE-side mirror mask has the same shape as `bands`: triangle indices into THIS chunk's
    // OLD index buffer. The buffer is now a fresh one and the indices are invalid — restoring them
    this.invalidateFineMasksFor(key);
    this.requeueReaders(key);
    this.pending.add(key);
    // Invalidate caches that depend on this chunk's state.
    this.invalidateFinerCache(key);
    this.invalidateRequiredFor(key);
    const { cx, cy, cz, lod } = this.coordsOf(key);
    for (const dir of SEAM_DIRS) {
      const nbrKey = chunkKeyLod(cx + dir.dx, cy + dir.dy, cz + dir.dz, lod);
      if (this.geometries.get(nbrKey)?.hasGeometry()) this.pending.add(nbrKey);
    }
  }

  /** A chunk's DISPLAY state flipped (the walk showed or hid it). The ownership predicate is */
  onDisplayChanged(key: string): void {
    this.flipped.add(key);
    const geo = this.geometries.get(key);
    const { cx, cy, cz, lod } = this.coordsOf(key);
    if (geo?.hasGeometry()) this.crossPending.add(key);
    // A strip that RESOLVED INTO this key, or handed it a row, was built against the other verdict.
    this.requeueReaders(key);
    // Display changes affect the required-skirts computation.
    this.invalidateRequiredFor(key);
    for (const dir of SEAM_DIRS) {
      const nbrKey = chunkKeyLod(cx + dir.dx, cy + dir.dy, cz + dir.dz, lod);
      if (this.geometries.get(nbrKey)?.hasGeometry()) this.crossPending.add(nbrKey);
    }
    // Showing or hiding a COARSE chunk changes the ownership of every boundary finer under it.
    if (lod > 0) this.enqueueFinerNeighbours(cx, cy, cz, lod);
  }

  /**
   * THE SKIRT OWNERS A DISPLAY FLIP OF `key` WOULD MAKE RESPONSIBLE — the chunk itself when it is a
   * coarse chunk with geometry, plus the registered coarser neighbour across each face. This is the
   * same derivation reconcileCoarseSkirts uses to build its `priority` set (the owners exempt from the
   * flush budget, because a chunk shown this frame must get its seam this frame — C4), exposed so the
   * DisplayCommit can CHARGE a transaction for it BEFORE admitting the flip. Without that charge the
   * commit admitted flips by their own cost (an index write, microseconds) and the flush then owed an
   * unbudgeted skirt per owner: a ring arriving flipped 100+ coarse chunks in one commit and the seam
   * build ran 18-40 ms in that frame — the "grouper" spike as the far rings land.
   */
  priorityOwnersFor(key: string): string[] {
    const out: string[] = [];
    const { cx, cy, cz, lod } = this.coordsOf(key);
    if (lod > 0 && this.geometries.has(key)) out.push(key);
    const registered = (k: string): boolean => !!this.geometries.get(k)?.hasGeometry();
    for (const dir of SEAM_DIRS) {
      const c = coarserNeighbourOf(cx, cy, cz, lod, dir, registered);
      if (c) out.push(c.key);
    }
    return out;
  }

  /**
   * Reconcile all queued chunks against their neighbors, then clear the queue.
   * Its writes reach the screen through the notify closures (TerrainBatch.refreshChunk / setApron).
   */
  flush(budgetEnd?: number): void {
    // THE PER-FLUSH GAUGES DESCRIBE THIS FLUSH, including the quiet one. They used to be reset inside
    // `reconcileCoarseSkirts`, which neither early return below reaches, so a settled world kept
    this.lastSkirts = 0;
    this.lastSkirtTris = 0;
    this.lastSkirtOwners = 0;
    this.lastSkirtMasked = 0;
    this.lastSkirtDeferred = 0;
    this.lastSkirtPriority = 0;
    this.lastOwnerMs = 0;
    this.lastOwnerMaxMs = 0;
    this.lastOwnerMaxKey = '';
    this.lastSkirtComputeRan = 0;
    this.lastSkirtComputeSkipped = 0;
    this.lastSkirtFacesEmitted = 0;
    this.lastSkirtFacesSkipped = 0;
    Object.assign(this.seamCensus, newSeamCensus());
    this.coordMemo.clear();
    if (!this.enabled) {
      this.pending.clear();
      this.crossPending.clear();
      this.flipped.clear();
      return;
    }
    // An owed skirt (unresolved) keeps the flush alive even with both queues empty — retrying it
    // is the whole point of the set; the quiet case still exits here in one size check.
    if (this.pending.size === 0 && this.crossPending.size === 0 && this.unresolved.size === 0) {
      this.flipped.clear();
      return;
    }
    // A flush that threw last time left edits unpublished; land them before collecting more.
    if (this.flushing) this.publishDeferred();
    this.flushing = true;

    // The frame's clock (voxel/frameBudget.ts) — the display commit passes it through. No backlog
    // amplifier any more: the allowance itself grows when the frame has headroom, and a multiplier on
    // top of it was exactly the kind of private budget the frame clock exists to retire. Callers with
    // no frame (tests) get an unbounded flush.
    const wallBudgetEnd = budgetEnd ?? Number.POSITIVE_INFINITY;

    const fresh = this.pending;

    // ---- SAME LOD: reconcile NORMALS, and nothing else ----
    for (const key of fresh) {
      const thisGeo = this.geometries.get(key);
      if (!thisGeo || !thisGeo.hasGeometry()) continue;
      // Same-LOD seams only: lod-keyed neighbours never match across LOD levels, and a cross-LOD
      // boundary is the coarse owner's business (reconcileCoarseSkirts below).
      const { cx, cy, cz, lod } = this.coordsOf(key);

      for (const dir of SEAM_DIRS) {
        const nbrKey = chunkKeyLod(cx + dir.dx, cy + dir.dy, cz + dir.dz, lod);
        const nbrGeo = this.geometries.get(nbrKey);
        if (!nbrGeo || !nbrGeo.hasGeometry()) continue;

        const nbrFresh = fresh.has(nbrKey);
        // Both fresh → AVERAGE, but process each unordered pair once (positive side).
        // Stale neighbor → COPY this chunk's seam normals into it (all directions).
        if (nbrFresh && !dir.positive) continue;

        const res = this.reconcileSeam(thisGeo, dir.thisFace, nbrGeo, dir.nbrFace, nbrFresh, lod);
        // Only re-merge a group whose normals ACTUALLY changed. A remesh that leaves
        // the shared seam normals unchanged (the common case once a seam has settled)
        if (res.thisChanged) this.normalsDirty.add(key);
        if (res.nbrChanged) this.normalsDirty.add(nbrKey);
        // A skirt copies its shading from these very vertices, so a reconciled normal makes the
        // strip's stale. Only the signature is cleared, not the strip: the geometry is unchanged, so
        if (res.thisChanged) this.skirtSig.delete(key);
        if (res.nbrChanged) this.skirtSig.delete(nbrKey);
      }
    }

    // ---- CROSS-LOD: a RECONCILIATION over coarse owners, not a per-chunk walk ----
    //
    for (const key of fresh) {
      this.crossPending.add(key);
      const { cx, cy, cz, lod } = this.coordsOf(key);
      if (lod > 0) this.enqueueFinerNeighbours(cx, cy, cz, lod);
    }
    const scope = new Set(this.crossPending);
    for (const key of this.unresolved) scope.add(key);
    this.reconcileCoarseSkirts(scope, wallBudgetEnd, this.flipped);
    this.crossPending.clear();
    this.pending.clear();
    this.flipped.clear();
    this.publishDeferred();
  }

  // ==========================================================================
  // CROSS-LOD — the coarse chunk owns the seam (C3), and is the only side that gives way (C1)

  /** ONE RECONCILIATION PASS: what the displayed configuration REQUIRES, diffed against what is built. */
  private reconcileCoarseSkirts(
    scope: ReadonlySet<string>, budgetEnd: number, flipped: ReadonlySet<string> = new Set(),
  ): void {
    this.lastSkirts = 0;
    this.lastSkirtTris = 0;
    this.lastSkirtOwners = 0;
    this.lastSkirtMasked = 0;
    this.lastSkirtDeferred = 0;
    this.lastSkirtPriority = 0;
    if (scope.size === 0) return;

    // MEMOIZED FOR THIS PASS. Both predicates reach the scene graph through string-keyed maps, and the
    // enumeration below asks the same keys over and over: the fine chunks inside one coarse cell all
    // name the same coarse neighbour across a shared face, and a fine chunk is a candidate of several
    // owners. Safe because neither answer can change while it is being read: the flush runs after the
    // commit's flips and neither displays a chunk nor registers geometry, so a requirement computed
    // in the build loop (see requiredFor) reads the same world the preamble did.
    const regMemo = this.regMemo; regMemo.clear();
    const drawMemo = this.drawMemo; drawMemo.clear();
    this.drawMemoP.clear();
    const registered = (k: string): boolean => {
      const got = regMemo.get(k);
      if (got !== undefined) return got;
      const v = !!this.geometries.get(k)?.hasGeometry();
      regMemo.set(k, v);
      return v;
    };
    const drawable = (k: string): boolean => {
      const got = drawMemo.get(k);
      if (got !== undefined) return got;
      const v = registered(k) && this.isDisplayed(k);
      drawMemo.set(k, v);
      return v;
    };

    const candidates = new Set<string>();
    for (const key of scope) {
      const { cx, cy, cz, lod } = this.coordsOf(key);
      // At ANY lod: a coarse owner owes a seam, and a fine chunk may still owe a diagonal block mask
      // over a coarser displayed neighbour.
      if (this.geometries.has(key)) candidates.add(key);
      // The coarser chunks THIS key abuts. Registered rather than drawable: an owner whose mesh has
      // landed but whose display flip has not is exactly the case the retry set exists for, and
      for (const dir of SEAM_DIRS) {
        const c = coarserNeighbourOf(cx, cy, cz, lod, dir, registered);
        if (c) candidates.add(c.key);
      }
      // ...and the diagonal margin holders SEAM_DIRS cannot reach, up = 0 (same-lod) included.
      for (let up = 0; up <= 2; up++) {
        for (const d of DIAGONAL_MARGIN_OFFSETS) {
          const k = chunkKeyLod((cx >> up) - d[0], (cy >> up) - d[1], (cz >> up) - d[2], lod + up);
          if (this.geometries.has(k)) candidates.add(k);
        }
      }
    }

    // THE OWNERS THIS COMMIT'S FLIPS MADE RESPONSIBLE — derived from `flipped` exactly as candidates
    // are from scope, so the two agree on what an owner is. These are exempt from the budget: a chunk
    // shown this frame gets its seam this frame or the hole is visible.
    const priority = new Set<string>();
    for (const key of flipped) {
      const { cx, cy, cz, lod } = this.coordsOf(key);
      // Every shape `candidates` derives from scope, or the owner a flip made responsible waits for
      // the budget: a lod-0 flip owes its own diagonal block mask, and the holder of the block over
      // a flipped chunk is reachable by no face.
      if (this.geometries.has(key)) priority.add(key);
      for (const dir of SEAM_DIRS) {
        const c = coarserNeighbourOf(cx, cy, cz, lod, dir, registered);
        if (c) priority.add(c.key);
      }
      for (let up = 0; up <= 2; up++) {
        for (const d of DIAGONAL_MARGIN_OFFSETS) {
          const k = chunkKeyLod((cx >> up) - d[0], (cy >> up) - d[1], (cz >> up) - d[2], lod + up);
          if (this.geometries.has(k)) priority.add(k);
        }
      }
    }

    // WHAT EACH CANDIDATE REQUIRES, one owner at a time, served from the memo unless this frame's
    // changes reached that owner (see invalidateRequiredFor). The whole-set form this replaces was
    // all-or-nothing: one unstamped candidate re-enumerated every other candidate too, and while the
    // player moves there is always one.
    //
    // NOTE: we deliberately do NOT force a recompute when `unresolved` is non-empty. A retry set
    // holds OWNERS whose build missed, and the candidate set above already folds them into scope.
    const lodOfKey = (k: string): number => this.coordsOf(k).lod;
    const finerOf = (k: string, face: number): FinerCandidates => this.finerNeighboursOf(k, face);
    // WHEN THE LOOP REACHES THE OWNER, not for every candidate up front. The enumeration is six faces
    // of up to twenty finer candidates each, with a display probe per candidate, and it used to run
    // UNBUDGETED for the whole candidate set — while a zoom step names hundreds of owners and the
    // budget then refuses most of them, so most of that work was for owners the flush never built.
    // Memoised per owner exactly as before (requiredByOwner, invalidated by invalidateRequiredFor).
    const requiredFor = (owner: string): RequiredSkirt[] => {
      const { cx, cy, cz, lod } = this.coordsOf(owner);
      const packed = packChunkKeyLod(cx, cy, cz, lod);
      let recs = this.requiredByOwner.get(packed);
      if (recs === undefined) {
        recs = SkirtManager.computeOwnerSkirts(owner, drawable, lodOfKey, finerOf, ALL_LAYERS);
        this.requiredByOwner.set(packed, recs);
        this.lastSkirtComputeRan++;
      } else {
        this.lastSkirtComputeSkipped++;
      }
      return recs;
    };

    // Sort owners by distance to player so closest (most visible) ones are processed first within the
    // budget. Unresolved owners go first — they're debts that MUST be settled before new work, and
    // flip consequences ahead of even those: they are exempt from the budget, so running them early
    // leaves the remaining clock to the deferrable work rather than the other way round.
    //
    // ONE KEY PER OWNER, not one per comparison. The comparator did four string-Set probes and two
    // `distanceSqToCenter` calls — each of which parses the key to pack it — every time it ran, so
    // ordering n owners cost O(n log n) of work that only varies per owner. The index is the final
    // tiebreak so the order is fully determined rather than resting on the sort's stability.
    const owners = [...candidates];
    const tier = new Int8Array(owners.length);
    const dist = new Float64Array(owners.length);
    const order: number[] = [];
    for (let i = 0; i < owners.length; i++) {
      const o = owners[i];
      // Tier 3 is a lod-0 candidate: it can only ever owe a diagonal block mask, so it must never
      // take a budget slot from an owner that owes a seam.
      tier[i] = priority.has(o) ? 0 : this.unresolved.has(o) ? 1 : this.coordsOf(o).lod > 0 ? 2 : 3;
      dist[i] = this.distanceSqToCenter(o);
      order.push(i);
    }
    order.sort((x, y) => (tier[x] - tier[y]) || (dist[x] - dist[y]) || (x - y));
    const sortedOwners = order.map((i) => owners[i]);

    // WALL-CLOCK BUDGET — replaces the older item-count budget. Per-owner cost varies wildly (a
    // single owner with two cross-LOD faces and full liquid coverage is ~3× a no-op owner), so an
    let firstOwner = true;
    let processed = 0;
    for (const o of sortedOwners) {
      if (!firstOwner && !priority.has(o) && performance.now() >= budgetEnd) {
        // A held block is a hole, not a strip.
        if (this.diagBands.has(o)) this.maskDiagonalMargin(o);
        // ONLY A DEBT RIDES THE RETRY SET — filing every owner it could not reach is the self-feeding
        // queue that starves the ones that owe (pinned by crossLodSeam.integration). What it HOLDS
        // answers first and costs three map probes; the requirement enumeration is asked only of an
        // owner holding nothing, which is the one case that cannot be decided without it.
        if (this.skirtLayers.has(o) || this.bands.has(o) || this.diagBands.has(o)
          || requiredFor(o).length > 0) this.unresolved.add(o);
        continue;
      }
      firstOwner = false;
      processed++;
      const isPriority = priority.has(o);
      if (isPriority) this.lastSkirtPriority++;
      const t0 = performance.now();

      // An owner with no records is NOT REQUIRED, which the branch below reads as a missing `r` — so
      // an empty list must not pass as a requirement of zero layers.
      const recs = requiredFor(o);
      const r = recs.length > 0 ? recs : undefined;
      if (!r) {
        // NOT REQUIRED BY ANY FACE — which is not the same as owing nothing. A margin is a BLOCK, so
        // this chunk's + edges and + corner can overhang a finer region even when none of its faces
        // does, and the chunk diagonally off a region's corner is exactly that chunk.
        this.dropSkirts(o);
        this.maskDiagonalMargin(o);
        // Even without displayed finer neighbours, a registered participant with no mesh yet
        // is a consequence still owed (C4).
        if (this.ownerHasPendingParticipant(o)) {
          this.unresolved.add(o);
        } else {
          this.unresolved.delete(o);
        }
        this.noteOwnerTime(o, performance.now() - t0, isPriority);
        continue;
      }
      this.lastSkirtOwners++;
      this.buildOwnerSkirts(o, r);
      // ...and its own diagonal blocks, which its face bands do not reach: their cover comes from the
      // chunks across ONE face and spans that face's tangential cells 0..31, so the owner's tangential
      // margin is never backed and survives as a corner sliver.
      this.maskDiagonalMargin(o);
      this.noteOwnerTime(o, performance.now() - t0, isPriority);
    }
    this.lastSkirtDeferred = Math.max(0, sortedOwners.length - processed);
  }

  private noteOwnerTime(owner: string, ms: number, priority: boolean): void {
    if (priority) this.noteOwnerCost(ms);
    this.lastOwnerMs += ms;
    if (ms > this.lastOwnerMaxMs) { this.lastOwnerMaxMs = ms; this.lastOwnerMaxKey = owner; }
  }

  /** Smoothed cost of one budget-exempt owner, ms — what the DisplayCommit charges a flip for. */
  private ownerCostEmaMs = 1;

  private noteOwnerCost(ms: number): void {
    this.ownerCostEmaMs += (ms - this.ownerCostEmaMs) * 0.15;
  }

  estimatedOwnerCostMs(): number {
    return Math.max(0.05, this.ownerCostEmaMs);
  }

  /**
   * The chunks across one face of an owner that could be strictly finer — CANDIDATES only, filtered
   * by `computeRequiredSkirts`' displayed + strictly-finer test.
   */
  private finerNeighboursOf(key: string, face: number): FinerCandidates {
    const { cx, cy, cz, lod } = this.coordsOf(key);
    if (lod === 0) return EMPTY_CANDIDATES;
    const ownerPacked = packChunkKeyLod(cx, cy, cz, lod);
    let faces = this.finerCache.get(ownerPacked);
    if (faces === undefined) {
      faces = [undefined, undefined, undefined, undefined, undefined, undefined];
      this.finerCache.set(ownerPacked, faces);
    }
    const cached = faces[face];
    if (cached !== undefined) return cached;
    const result = this.computeFinerNeighbours(cx, cy, cz, lod, face);
    faces[face] = result;
    return result;
  }

  /** Raw computation of finer neighbours — separated for clarity and potential reuse. */
  private computeFinerNeighbours(
    cx: number, cy: number, cz: number, lod: number, face: number,
  ): FinerCandidates {
    const axis = faceAxis(face);
    const high = faceIsHigh(face);
    const a = (axis + 1) % 3, b = (axis + 2) % 3;
    const base = SCRATCH_BASE;
    base[0] = cx; base[1] = cy; base[2] = cz;
    const p = SCRATCH_POS;
    const keys: string[] = [];
    const lods: number[] = [];
    for (let fineLod = lod - 1; fineLod >= Math.max(0, lod - 2); fineLod--) {
      const n = 1 << (lod - fineLod);
      p[axis] = high ? base[axis] * n + n : base[axis] * n - 1;
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          p[a] = base[a] * n + i;
          p[b] = base[b] * n + j;
          keys.push(chunkKeyLod(p[0], p[1], p[2], fineLod));
          lods.push(fineLod);
        }
      }
    }
    return { keys, lods };
  }

  /** Is a skirt this owner does NOT currently owe nevertheless PENDING — a participant registered with */
  private ownerHasPendingParticipant(owner: string): boolean {
    if (!this.geometries.get(owner)?.hasGeometry()) return false;
    for (let face = 0; face < 6; face++) {
      for (const n of this.finerNeighboursOf(owner, face).keys) {
        if (meshInFlight(this.geometries.get(n))) return true;
      }
    }
    return false;
  }

  /** Retract everything a coarse owner holds: its ring mask first, then its strips. */
  private dropSkirts(key: string): void {
    this.skirtSig.delete(key);
    this.clearReads(key);
    this.bridgedOwners.delete(key);
    this.skirtFaceTris.delete(key);
    this.faceEmpty.delete(key);
    if (this.restoreBands(key)) {
      const g = this.geometries.get(key);
      if (g) this.notifyIndicesChanged(key, g);
    }
    // The FINE-side mirror mask must come down too: it lives on OTHER chunks' index buffers, but
    // is owned by THIS owner. Same one-commit rule as the OWNER mask — a mask is only correct
    this.restoreOwnerFineMasks(key);
    const layers = this.skirtLayers.get(key);
    if (!layers) return;
    for (const layer of layers) this.onSkirt(key, layer, null);
    this.skirtLayers.delete(key);
  }

  /** BUILD (or cache-hit) EVERY LAYER'S SKIRT FOR ONE OWNER, with its ring mask, in one visit. */
  private buildOwnerSkirts(owner: string, reqs: readonly RequiredSkirt[]): void {
    const geo = this.geometries.get(owner);
    if (!geo?.hasGeometry()) { this.unresolved.add(owner); return; }
    // Every record of one owner carries the same faces — computeRequiredSkirts decides them once and
    // fans them across the layers — so the face set and the mask are read from the first.
    const faces = reqs[0].faces;
    const participants: string[] = [];
    const seen = new Set<string>();
    for (const list of reqs[0].finerNeighbours) {
      for (const n of list) if (!seen.has(n)) { seen.add(n); participants.push(n); }
    }
    // Participants whose mesh has not landed: not in the signature as a clock (they have none), but
    // named in it, so the signature CHANGES the moment one of them gains geometry and the cache stops
    const pending: string[] = [];
    for (let face = 0; face < 6; face++) {
      for (const n of this.finerNeighboursOf(owner, face).keys) {
        if (meshInFlight(this.geometries.get(n)) && !pending.includes(n)) pending.push(n);
      }
    }

    // The flank is a source too: the corner rows resolve into it, and it lands after the owner.
    const flank = flankKeys(this.coordsOf(owner));

    const sigNow = (reads: readonly number[] | undefined): string => {
      let s = `${geo.meshGeneration}.${geo.indexEpoch ?? 0}#${faces.join('.')}`;
      for (const n of participants) {
        const g = this.geometries.get(n);
        s += `|${n}:${g?.meshGeneration ?? -1}.${g?.indexEpoch ?? 0}`;
      }
      // THE FLANK'S DISPLAY, not just its clock: a border row resolves into the flank, and a chunk
      // that was merely hidden when the row was built moves no meshGeneration when it is shown.
      s += '|f';
      for (const n of flank) {
        s += `.${this.geometries.get(n)?.meshGeneration ?? -1}${this.passDrawn(n) ? 'd' : ''}`;
      }
      if (pending.length) s += `|p:${pending.join(',')}`;
      // ...AND EVERY OTHER KEY THE LAST BUILD ASKED ABOUT — the patch a row was ceded to, the
      // ancestor a perpendicular plane was found under, the chunk a vertex resolved into.
      if (reads && reads.length) s += `|r${reads.length}.${this.readHash(reads)}`;
      return s;
    };
    // A QUIET RETRY COSTS A STRING COMPARE. The symmetric triggers bring every owner back here
    // whenever any neighbour moves, and the retry set brings the owed ones back unconditionally, so
    if (this.skirtSig.get(owner) === sigNow(this.skirtReads.get(owner))) {
      if (pending.length > 0) this.unresolved.add(owner); else this.unresolved.delete(owner);
      return;
    }

    // WHAT THIS BUILD READS, recorded as it reads it. Keys the signature already carries by name are
    // skipped, so the set stays the handful the rules reach OUTSIDE the owner's own dependencies.
    const reading = new Set<number>();
    const skip = new Set<number>();
    for (const n of participants) skip.add(this.packedOf(n));
    skip.add(this.packedOf(owner));
    for (const n of flank) skip.add(this.packedOf(n));
    this.reading = reading;
    this.readingSkip = skip;

    // ---- THE LOW SIDE OF EACH PLANE GIVES WAY (§4). The HIGH side is never masked. ----
    let touched = this.restoreBands(owner);
    this.restoreOwnerFineMasks(owner);
    const bandRecord = new Map<number, number[]>();
    const fineRecord = new Map<string, Map<number, number[]>>();
    // PER FACE, NOT PER LAYER — see `bridgedFaces`. A layer masked on the strength of one face used to
    // mask them ALL, so a face whose coarse side has no border at all was masked and handed to the
    const bridged = new Map<number, boolean[]>();
    for (const req of reqs) {
      const ok = this.bridgedFaces(geo, faces, req, req.layer);
      if (ok.some((b) => b)) bridged.set(req.layer, ok);
    }
    if (bridged.size > 0) this.bridgedOwners.add(owner); else this.bridgedOwners.delete(owner);
    let faceTris: Int32Array | undefined;
    if (bridged.size === 0) this.skirtFaceTris.delete(owner);
    else {
      faceTris = this.skirtFaceTris.get(owner);
      if (!faceTris) { faceTris = new Int32Array(2 * MISS_BASE); this.skirtFaceTris.set(owner, faceTris); }
      faceTris.fill(-1, 0, MISS_BASE);
      faceTris.fill(0, MISS_BASE);
    }
    for (const req of reqs) {
      const ok = bridged.get(req.layer);
      const list = bandRecord.get(req.layer) ?? [];
      const seen: number[] = [];
      for (let fi = 0; fi < faces.length; fi++) {
        const face = faces[fi];
        const high = faceIsHigh(face);
        if (!high && !ok?.[fi]) continue;
        const axis = faceAxis(face);
        if (high) {
          // Case H's overhang goes whether or not a seam follows, under the footprint only.
          if (seen.includes(axis)) continue;
          seen.push(axis);
          this.maskBand(geo, req.layer, axis, false, RING_MASK_CELLS, list,
            this.participantFootprint(owner, face, req.finerNeighbours[fi]));
        } else {
          // Case L: the finer chunks overhang UPWARD into the owner, so each of THEM is the low side
          // and the owner's cells 0-1 draw natively.
          for (const n of req.finerNeighbours[fi]) {
            this.maskParticipant(owner, n, req.layer, axis, fineRecord);
          }
        }
      }
      if (list.length) bandRecord.set(req.layer, list);
    }
    if (bandRecord.size > 0) touched = true;

    const had = this.skirtLayers.get(owner);
    const now = new Set<number>();
    for (const req of reqs) {
      // A LAYER WITH NO SURFACE AT THE BOUNDARY IS NOTHING TO DO, NOT A FAILURE. Conflating the two
      // is what let 40% of faces go unbridged while every test passed under fine ownership, and it is
      const faceOk = bridged.get(req.layer);
      if (!faceOk) continue;
      const skirt = this.buildLayerSkirt(owner, geo, faces, faceOk, req, faceTris);
      if (!skirt) {
        if (faceTris) {
          for (let f = 0; f < 6; f++) {
            const i = f * LAYER_COUNT + req.layer;
            if (faceTris[i] > 0) faceTris[i] = 0;
          }
        }
        // The case-L participants were masked on the strength of a seam that never came.
        for (const [fine, per] of fineRecord) {
          const fineTris = per.get(req.layer);
          const fineGeo = this.geometries.get(fine);
          if (!fineTris || !fineGeo) continue;
          if (this.restoreLayerBand(fineGeo, req.layer, fineTris)) per.delete(req.layer);
        }
        continue;
      }
      now.add(req.layer);
      this.lastSkirts++;
      this.lastSkirtTris += skirt.indices.length / 3;
      this.onSkirt(owner, req.layer, skirt);
    }
    if (had) for (const layer of had) if (!now.has(layer)) this.onSkirt(owner, layer, null);
    if (now.size > 0) this.skirtLayers.set(owner, now); else this.skirtLayers.delete(owner);
    // A bridged owner whose every layer emitted nothing owes nothing either.
    if (now.size === 0 && pending.length === 0) this.bridgedOwners.delete(owner);

    // THE INDEX EDIT IS PUBLISHED ONCE, AFTER THE WITHDRAWALS. Vertices did not move; the DRAWN
    // TRIANGLES did, so the merged copy needs this owner's index range re-copied — the position/normal
    if (bandRecord.size > 0) this.bands.set(owner, bandRecord);
    if (touched) {
      this.notifyIndicesChanged(owner, geo);
      this.lastSkirtMasked += [...bandRecord.values()].reduce((n, l) => n + l.length, 0);
    }
    // The participants' index edits ride the same commit — and their indexEpoch is part of `sigNow`
    // below, which is what keeps a steady frame inert instead of re-masking forever.
    this.commitParticipantMasks(owner, fineRecord);

    this.reading = null;
    this.readingSkip = null;
    const reads = this.noteReads(owner, reading);

    // POST-MASK, and UNCONDITIONALLY — a MISS IS NOT A DEBT.
    //
    this.skirtSig.set(owner, sigNow(reads));
    if (pending.length > 0) this.unresolved.add(owner);
    else this.unresolved.delete(owner);
  }

  /** File one build's read set both ways round, replacing the previous one. */
  private noteReads(owner: string, reading: ReadonlySet<number>): number[] | undefined {
    this.clearReads(owner);
    if (reading.size === 0) return undefined;
    const reads = [...reading];
    this.skirtReads.set(owner, reads);
    for (const k of reads) {
      const set = this.readBy.get(k);
      if (set) set.add(owner); else this.readBy.set(k, new Set([owner]));
    }
    return reads;
  }

  private clearReads(owner: string): void {
    const old = this.skirtReads.get(owner);
    if (!old) return;
    for (const k of old) {
      const set = this.readBy.get(k);
      if (!set) continue;
      set.delete(owner);
      if (set.size === 0) this.readBy.delete(k);
    }
    this.skirtReads.delete(owner);
  }

  /** THE DEBT A DISPLAY FLIP SETTLES: every owner whose last build read this key comes back. */
  private requeueReaders(key: string): void {
    const readers = this.readBy.get(this.packedOf(key));
    if (!readers) return;
    for (const o of readers) if (this.geometries.get(o)?.hasGeometry()) this.crossPending.add(o);
  }

  /** `drawn`, served from the per-pass memo: no display verdict can change inside one flush. */
  private passDrawn(key: string): boolean {
    const got = this.drawMemo.get(key);
    if (got !== undefined) return got;
    const v = !!this.geometries.get(key)?.hasGeometry() && this.isDisplayed(key);
    this.drawMemo.set(key, v);
    return v;
  }

  private passDrawnP(packed: number): boolean {
    const got = this.drawMemoP.get(packed);
    if (got !== undefined) return got;
    const v = !!this.geometryByPacked(packed)?.hasGeometry() && this.isDisplayedPacked(packed);
    this.drawMemoP.set(packed, v);
    return v;
  }

  /** The display+mesh identity of everything one build read, as one number. */
  private readHash(keys: readonly number[]): number {
    let h = 0;
    for (let i = 0; i < keys.length; i++) {
      const g = this.passDrawnP(keys[i]) ? this.geometryByPacked(keys[i]) : undefined;
      h = (Math.imul(h, 0x01000193) ^ (g ? (g.meshGeneration | 0) + 1 : 0)) | 0;
    }
    return h;
  }

  /** Record one display verdict this build rests on. See the `reading` window in buildOwnerSkirts. */
  private trackRead(packed: number): void {
    const r = this.reading;
    if (r !== null && !this.readingSkip!.has(packed)) r.add(packed);
  }

  /** The participants' span in the owner's cells — only there is the overhang duplicate. */
  private participantFootprint(
    owner: string, face: number, parts: readonly string[],
  ): number[] {
    const o = this.coordsOf(owner);
    const axis = faceAxis(face);
    const oc = [o.cx, o.cy, o.cz];
    const t1 = (axis + 1) % 3, t2 = (axis + 2) % 3;
    const out: number[] = [];
    for (const n of parts) {
      const p = this.coordsOf(n);
      const d = o.lod - p.lod;
      if (d < 1) continue;
      const pc = [p.cx, p.cy, p.cz];
      const size = CHUNK_SIZE >> d;
      out.push((pc[t1] - (oc[t1] << d)) * size, (pc[t2] - (oc[t2] << d)) * size, size);
    }
    return out;
  }

  /** Which faces want a skirt. Case H asks only the owner: its rim is the case the seam must cap. */
  private bridgedFaces(
    geo: ChunkGeometry, faces: readonly number[], req: RequiredSkirt, layer: number,
  ): boolean[] {
    const ok = faces.map(() => false);
    if (!geo.getLayerGeometry(layer)) return ok;
    for (let fi = 0; fi < faces.length; fi++) {
      const face = faces[fi];
      const axis = faceAxis(face);
      // The SEAM CELL LAYERS of spec §3: the owner's 31/0 and the participant's 0/31 by case.
      const high = faceIsHigh(face);
      const ownCell = high ? CHUNK_SIZE - 1 : 0;
      if (!this.hasBorderSurface(geo, layer, axis, ownCell)) continue;
      if (high) { ok[fi] = true; continue; }
      for (const n of req.finerNeighbours[fi]) {
        const nGeo = this.geometries.get(n);
        if (!nGeo?.getLayerGeometry(layer)) continue;
        if (this.hasBorderSurface(nGeo, layer, axis, CHUNK_SIZE - 1)) { ok[fi] = true; break; }
      }
    }
    return ok;
  }

  /**
   * IS THERE A SURFACE IN THIS BORDER CELL LAYER — counted from `cellIndices`, which holds every
   * vertex, not from the boundary CSR, which holds only the DRAWN ones.
   *
   * `pushBoundary` runs inside the triangle loop, so a vertex retained by `isBoundaryCell` but used by
   * no triangle is absent from the CSR. Those are exactly the vertices a cross-LOD seam welds to — it
   * is why they are retained — and asking the CSR meant a face whose border layer draws nothing was
   * declared unbridged. No seam was built for it, no mask was applied, and the whole seam band went
   * missing rather than one cell of it. That is the state `skipHighBoundary` leaves a chunk in when its
   * + neighbour's voxels never arrived, and the state a grazing surface leaves it in when the cells one
   * layer inward hold no vertices, so the mesher's four-cell quad rule never fires.
   *
   * `cell` must be one the digest's border band carries; an interior one reads as empty.
   */
  private hasBorderSurface(
    geo: ChunkGeometry, layer: number, axis: number, cell: number,
  ): boolean {
    const d = digestFor(geo, layer);
    if (!d) return false;
    // The digest groups the border band by cell, so the count the scan used to reach is a subtraction
    // of two offsets. Vertices, not distinct cells — the threshold means what it did.
    return bandCount(d, axis, cell) >= 2;
  }

  /**
   * ONE (owner, layer) SKIRT: the FIELD's faces at the shared plane, cornered on both meshes.
   *
   * One `emitSeamFace` per bridged face into one pooled strip, so the owner's own corners stay
   * internal to a single strip exactly as before. See docs/cross-lod-seam-redesign.md §§3-5.
   */
  private buildLayerSkirt(
    owner: string, geo: ChunkGeometry,
    faces: readonly number[], faceOk: readonly boolean[], req: RequiredSkirt,
    faceTris?: Int32Array,
  ): ApronMesh | null {
    const layer = req.layer;
    if (!geo.getLayerGeometry(layer) || !geo.getCellIndices(layer)) return null;
    const strip = emptySeamStrip();
    const sources: SeamSource[] = [];
    const sourceOf = new Map<number, number>();
    // The PREVIOUS build's read set, which is still filed at this point (noteReads runs after) — the
    // conservative term of the per-face signature: any cross-owner resolution that moved invalidates
    // every remembered-empty face of this owner rather than only the face that read it.
    const readTag = this.readHash(this.skirtReads.get(owner) ?? EMPTY_READS);
    let empties = this.faceEmpty.get(owner);
    for (let fi = 0; fi < faces.length; fi++) {
      if (!faceOk[fi]) continue;
      // A participant with nothing in this layer still bounds the case-H coarse rim scan.
      const high = faceIsHigh(faces[fi]);
      const axis = faceAxis(faces[fi]);
      const slot = faces[fi] * LAYER_COUNT + layer;
      const parts: string[] = [];
      let fineSurface = false;
      for (const n of req.finerNeighbours[fi]) {
        const g = this.geometries.get(n);
        if (!g) continue;
        if (g.getLayerGeometry(layer) && g.getCellIndices(layer)) {
          parts.push(n);
          if (faceTris && !fineSurface) {
            fineSurface = this.hasBorderSurface(g, layer, axis, high ? 0 : CHUNK_SIZE - 1);
          }
        } else if (high && g.hasGeometry()) parts.push(n);
      }
      if (faceTris) faceTris[slot] = fineSurface ? 0 : -2;
      if (parts.length === 0) continue;
      // A FACE THAT DREW NOTHING IS STILL A FULL PLANE SCAN, and one arriving participant re-runs
      // every face of every layer of its owner (the signature is per owner). A face whose own inputs
      // are unchanged since it last emitted NOTHING cannot emit anything now, so it is skipped with
      // its audit counts restored — the emitter is the cost, and an empty result needs no geometry
      // kept to reuse it. Faces that DO emit are never cached: their triangles live in the strip.
      let fsig = `${geo.meshGeneration}.${geo.indexEpoch ?? 0}#${readTag}`;
      for (const n of parts) {
        const g = this.geometries.get(n);
        fsig += `|${g?.meshGeneration ?? -1}.${g?.indexEpoch ?? 0}`;
      }
      const cached = empties?.get(slot);
      if (cached !== undefined && cached.sig === fsig) {
        if (faceTris) {
          faceTris[slot] = cached.tris;
          faceTris[MISS_BASE + slot] = cached.miss;
        }
        this.lastSkirtFacesSkipped++;
        continue;
      }
      const before = strip.indexCount;
      const cen = this.seamCensus;
      const c0 = cen.crossings, h0 = cen.hidden;
      const apex0 = cen.noCoarseApex;
      const gone0 = cen.noChunk + cen.noFineVertex + cen.bandNoVertex;
      this.emitFaceSeam(owner, faces[fi], layer, parts, strip, sources, sourceOf);
      const added = strip.indexCount - before;
      this.lastSkirtFacesEmitted++;
      if (faceTris) {
        const apex = cen.noCoarseApex - apex0;
        const gone = cen.noChunk + cen.noFineVertex + cen.bandNoVertex - gone0;
        // ROWS REFUSED FOR WANT OF A DRAWN SOURCE — the naked joint, counted where it happens.
        faceTris[MISS_BASE + slot] = apex + gone;
        if (fineSurface) {
          const tris = added / 3;
          // NOTHING TO DRAW is not nakedness: a plane whose every crossing sits behind an opaque cell
          // (liquid under rock) has no seam, and neither has one with no crossing at all. A face that
          // drew nothing because the coarse contour held no vertex to weld to is the apex disagreement,
          // counted apart from a boundary nobody clothed.
          faceTris[slot] = tris > 0 ? tris
            : cen.crossings - c0 === cen.hidden - h0 ? -3
            : apex > 0 && gone === 0 ? -4
            : 0;
        }
      }
      if (added === 0) {
        if (!empties) { empties = new Map(); this.faceEmpty.set(owner, empties); }
        empties.set(slot, {
          sig: fsig,
          tris: faceTris ? faceTris[slot] : 0,
          miss: faceTris ? faceTris[MISS_BASE + slot] : 0,
        });
      } else empties?.delete(slot);
    }
    if (strip.indexCount === 0) return null;
    return this.stripToApron(strip, layer);
  }

  /** One chunk's grid for a face's samples: its data, and its + neighbours' resolved on first use. */
  /**
   * POOLED, NOT ALLOCATED. A face takes one sampler for the owner and one per participant — up to
   * twenty-one, times six faces, times three layers, for every owner of a flush — and a fresh object
   * with a fresh eight-slot array each time is garbage the emitter never used to make.
   * `samplerCursor` is reset per face, so a sampler outlives only the face that took it.
   */
  private readonly samplerPool: GridSampler[] = [];
  private samplerCursor = 0;

  private samplerFor(cx: number, cy: number, cz: number, lod: number): GridSampler {
    const c = this.getChunkAt(cx, cy, cz, lod);
    let s = this.samplerPool[this.samplerCursor];
    if (s === undefined) {
      s = { cx, cy, cz, lod, data: null, nbr: [undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined] };
      this.samplerPool.push(s);
    }
    this.samplerCursor++;
    s.cx = cx; s.cy = cy; s.cz = cz; s.lod = lod;
    s.data = c ? c.data : null;
    const n = s.nbr;
    n[1] = n[2] = n[3] = n[4] = n[5] = n[6] = n[7] = undefined;
    return s;
  }

  /** The voxel THE MESHER SAW: lattice 32 comes from the + neighbour, or the margin policy when it is
   *  not resident — the same answer the mesher's expandChunkData gave, so the seam's signs match it. */
  private sampleGrid(s: GridSampler, lx: number, ly: number, lz: number): number {
    if (!s.data) return -1;
    const ox = lx >= CHUNK_SIZE ? 1 : 0, oy = ly >= CHUNK_SIZE ? 1 : 0, oz = lz >= CHUNK_SIZE ? 1 : 0;
    const slot = ox | (oy << 1) | (oz << 2);
    if (slot === 0) return s.data[voxelIndex(lx, ly, lz)];
    let src = s.nbr[slot];
    if (src === undefined) {
      const n = this.getChunkAt(s.cx + ox, s.cy + oy, s.cz + oz, s.lod);
      src = n ? n.data : marginPolicyData(s.cx + ox, s.cy + oy, s.cz + oz, s.lod);
      s.nbr[slot] = src;
    }
    return marginVoxelFrom(src, s.data, lx, ly, lz);
  }

  /**
   * ONE MESH VERTEX BY CELL, WORLD-GLOBAL. `cell` is the global cell coordinate triple at `lod`; the
   * answer is whichever DISPLAYED chunk holds that volume, searched outward from `lod` because a
   * flank cell, a region corner or a mixed-level face lands on a chunk at some other level.
   */
  private seamVertexAt(
    lod: number, axis: number, c1: number, c2: number, layer: number,
    plane0: number, high: boolean, sources: SeamSource[], sourceOf: Map<number, number>,
    margin: boolean,
  ): number {
    let sawChunk = false;
    // Pass 1 reaches into a tangential neighbour's margin, which holds the same lattice vertex.
    for (let pass = 0; pass < (margin ? 2 : 1); pass++) {
    for (let step = 0; step <= RESOLVE_LOD_REACH * 2; step++) {
      // lod, then coarser, then finer: the nearest level owning the volume is the surface abutting it.
      const l = step === 0 ? lod
        : step <= RESOLVE_LOD_REACH ? lod + step : lod - (step - RESOLVE_LOD_REACH);
      if (l < 0 || l > MAX_PACKABLE_LOD) continue;
      const sh = l - lod;
      const a = high ? (plane0 >> l) : (plane0 >> l) - 1;
      const n = sh >= 0 ? 1 : 1 << -sh;
      const b1 = sh >= 0 ? c1 >> sh : c1 << -sh;
      const b2 = sh >= 0 ? c2 >> sh : c2 << -sh;
      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          for (let alt = pass; alt < (pass === 0 ? 1 : 4); alt++) {
            const p1 = b1 + i, p2 = b2 + j;
            const s1 = alt & 1, s2 = (alt >> 1) & 1;
            const q1 = (p1 & 31) + (s1 ? CHUNK_SIZE : 0), q2 = (p2 & 31) + (s2 ? CHUNK_SIZE : 0);
            if (q1 >= GRID || q2 >= GRID) continue;
            const co = SCRATCH_CELL;
            co[axis] = a >> 5;
            co[(axis + 1) % 3] = (p1 >> 5) - s1;
            co[(axis + 2) % 3] = (p2 >> 5) - s2;
            const packed = packChunkKeyLod(co[0], co[1], co[2], l);
            this.trackRead(packed);
            const geo = this.geometryByPacked(packed);
            if (!geo?.hasGeometry() || !this.isDisplayedPacked(packed)) continue;
            sawChunk = true;
            const digest = digestFor(geo, layer);
            if (!digest) continue;
            const cell = a & 31;
            const low = cell < RIM_DEPTHS;
            const slot = low ? cell : BORDER_LOW_CELL - cell;
            if (slot < 0 || slot >= RIM_DEPTHS) continue;
            const tab = rimTable(digest, axis, low);
            const v = tab[(slot * GRID + q1) * GRID + q2];
            if (v < 0) continue;
            let si = sourceOf.get(packed);
            if (si === undefined) {
              const g = geo.getLayerGeometry(layer);
              if (!g) continue;
              const o = geo.chunk.getWorldPosition();
              si = sources.length;
              sources.push({
                // The one place a string is built here: once per source chunk, not per attempt.
                key: chunkKeyLod(co[0], co[1], co[2], l),
                positions: g.getAttribute('position').array as Float32Array,
                normals: g.getAttribute('normal').array as Float32Array,
                ox: o.x, oy: o.y, oz: o.z,
                slot: -1,
              });
              sourceOf.set(packed, si);
            }
            return (si << SRC_SHIFT) | v;
          }
        }
      }
    }
    }
    return sawChunk ? RESOLVE_NO_VERTEX : RESOLVE_NO_CHUNK;
  }

  /** Is the volume of chunk (cx,cy,cz,lod) drawn by a COARSER chunk - the owner across that face? */
  private coarserDrawnOver(cx: number, cy: number, cz: number, lod: number): boolean {
    let px = cx, py = cy, pz = cz;
    for (let l = lod + 1; l <= MAX_PACKABLE_LOD; l++) {
      px >>= 1; py >>= 1; pz >>= 1;
      if (this.drawnP(packChunkKeyLod(px, py, pz, l))) return true;
    }
    return false;
  }

  /** Assemble one (owner face, layer) SeamFieldInput and emit it into `strip`. */
  private emitFaceSeam(
    owner: string, face: number, layer: number, parts: readonly string[],
    strip: SeamStrip, sources: SeamSource[], sourceOf: Map<number, number>,
  ): void {
    this.vertexMemo.clear();
    this.samplerCursor = 0;
    const o = this.coordsOf(owner);
    const oc = [o.cx, o.cy, o.cz];
    const axis = faceAxis(face);
    const caseH = faceIsHigh(face);
    const t1 = (axis + 1) % 3, t2 = (axis + 2) % 3;
    // The shared plane as a GLOBAL LOD-0 voxel coordinate: the owner's lattice 32 (case H) or 0
    // (case L). `>> l` gives it at any level, which is every coordinate this face needs.
    const plane0 = ((caseH ? oc[axis] + 1 : oc[axis]) * CHUNK_SIZE) << o.lod;

    const patches: SeamPatch[] = [];
    // The participant's own chunk coords, parallel to `patches` — `hasPatchAt` asks the world about
    // the chunk one step along +t, not just this owner's list, so the boundary row between two
    // owners on one plane belongs to exactly one of them (spec §5.3).
    const pat1: number[] = [], pat2: number[] = [], patA: number[] = [];
    // THE GRID EACH SAMPLE READS, RESOLVED ONCE PER CHUNK, NOT ONCE PER SAMPLE: every one of a patch's
    // thousand plane samples used to look its chunk up again, twice when the sample fell in a margin.
    const ownerS = this.samplerFor(o.cx, o.cy, o.cz, o.lod);
    const partS: GridSampler[] = [];
    for (const n of parts) {
      const p = this.coordsOf(n);
      const pc = [p.cx, p.cy, p.cz];
      patches.push({ base1: pc[t1] * CHUNK_SIZE, base2: pc[t2] * CHUNK_SIZE, lod: p.lod });
      pat1.push(pc[t1]); pat2.push(pc[t2]); patA.push(pc[axis]);
      partS.push(this.samplerFor(p.cx, p.cy, p.cz, p.lod));
    }
    const nc = SCRATCH_NBR;

    /** Is there a perpendicular cross-LOD plane, and an owner on it, at that border row? */
    const perpOwner = (pi: number, dir: number, side: number): boolean => {
      const t = dir === 0 ? t1 : t2;
      nc[axis] = patA[pi];
      nc[t1] = pat1[pi];
      nc[t2] = pat2[pi];
      nc[t] += side === 0 ? -1 : 1;
      return this.coarserDrawnOver(nc[0], nc[1], nc[2], patches[pi].lod);
    };

    const g = SCRATCH_VOXEL;
    const census = this.seamCensus;
    const onEvent = this.onSeamEvent;
    emitSeamFace({
      ownerLod: o.lod,
      face,
      layer,
      patches,
      sources,
      // Every sample is ONE chunk's own grid 0..CHUNK_SIZE, never a neighbour's data directly.
      planeVoxel: (pi, r1, r2) => {
        g[axis] = caseH ? 0 : CHUNK_SIZE;
        g[t1] = r1;
        g[t2] = r2;
        return this.sampleGrid(partS[pi], g[0], g[1], g[2]);
      },
      coarsePlaneVoxel: (q1, q2) => {
        g[axis] = caseH ? CHUNK_SIZE : 0;
        g[t1] = q1 - oc[t1] * CHUNK_SIZE;
        g[t2] = q2 - oc[t2] * CHUNK_SIZE;
        return this.sampleGrid(ownerS, g[0], g[1], g[2]);
      },
      lowVoxel: (pi, b1, b2) => {
        g[axis] = CHUNK_SIZE - 1;
        if (caseH) {
          g[t1] = b1 - oc[t1] * CHUNK_SIZE;
          g[t2] = b2 - oc[t2] * CHUNK_SIZE;
          return this.sampleGrid(ownerS, g[0], g[1], g[2]);
        }
        g[t1] = b1 - pat1[pi] * CHUNK_SIZE;
        g[t2] = b2 - pat2[pi] * CHUNK_SIZE;
        return this.sampleGrid(partS[pi], g[0], g[1], g[2]);
      },
      bandVoxel: (pi, b1, b2, depth) => {
        g[axis] = depth;
        if (caseH) {
          g[t1] = b1 - pat1[pi] * CHUNK_SIZE;
          g[t2] = b2 - pat2[pi] * CHUNK_SIZE;
          return this.sampleGrid(partS[pi], g[0], g[1], g[2]);
        }
        g[t1] = b1 - oc[t1] * CHUNK_SIZE;
        g[t2] = b2 - oc[t2] * CHUNK_SIZE;
        return this.sampleGrid(ownerS, g[0], g[1], g[2]);
      },
      resolveVertex: (side, pi, c1, c2) => {
        const fine = side === SIDE_FINE;
        const l = fine ? patches[pi].lod : o.lod;
        const memoKey = ((c1 + VMEMO_OFFSET) * VMEMO_STRIDE + (c2 + VMEMO_OFFSET)) * 32
          + (l * 2 + side);
        const got = this.vertexMemo.get(memoKey);
        if (got !== undefined) return got;
        // The seam cell layer is the one ABOVE the plane on the high side, below it on the low one.
        const high = caseH === fine;
        const v = this.seamVertexAt(l, axis, c1, c2, layer, plane0, high, sources, sourceOf, fine);
        this.vertexMemo.set(memoKey, v);
        return v;
      },
      hasPatchAt: (pi, dir) => {
        nc[axis] = patA[pi];
        nc[t1] = pat1[pi] + (dir === 0 ? 1 : 0);
        nc[t2] = pat2[pi] + (dir === 1 ? 1 : 0);
        return this.drawnP(packChunkKeyLod(nc[0], nc[1], nc[2], patches[pi].lod));
      },
      // The zip's corner line goes to the lower face axis — a tie-break needing no agreement (§5.3).
      cedesRow: (pi, dir, side) => {
        const t = dir === 0 ? t1 : t2;
        if (t > axis) return false;
        return perpOwner(pi, dir, side);
      },
      // At case L the corner line's band is still this owner's: no neighbour reaches across it.
      cedesBandRow: (pi, dir, side) => (caseH || side === 0) && perpOwner(pi, dir, side),
      trace: onEvent === null ? undefined : (ev) => {
        const l = ev.unit === 1 ? o.lod : (patches[ev.pi]?.lod ?? o.lod);
        const s = VOXEL_SCALE * (1 << l);
        const w = SCRATCH_TRACE;
        w[axis] = plane0 * VOXEL_SCALE;
        w[t1] = ev.c1 * s;
        w[t2] = ev.c2 * s;
        onEvent({
          owner, face, layer, ownerLod: o.lod, fineLod: patches[ev.pi]?.lod ?? o.lod,
          wx: w[0], wy: w[1], wz: w[2], ev,
        });
      },
    }, strip, census);
  }

  /** A strip becomes a renderable apron: positions straight through (they are already world, and every */
  private stripToApron(strip: SeamStrip, layer: number): ApronMesh | null {
    const n = strip.vertexCount;          // NOT positions.length/3 — that is capacity
    const attrs: Record<string, AttrArray> = {};
    // PROVENANCE RESOLVES ONCE PER (OWNER, ATTRIBUTE). A strip draws from at most a handful of
    // chunks — the owner, its finer neighbours, its flank — so the whole mapping is a few dozen
    // lookups; the per-vertex form this replaces built a `${key}|${name}` string and probed a map
    // for every vertex of every attribute, then again per triangle corner for the material pair.
    const geos = strip.srcOwners.map((k) => this.geometries.get(k)?.getLayerGeometry(layer) ?? null);
    const srcArrays = (name: string): (AttrArray | undefined)[] =>
      geos.map((g) => g?.getAttribute(name)?.array as AttrArray | undefined);
    // EVERY ATTRIBUTE EXCEPT THE MATERIAL PAIR is a per-vertex copy from the vertex's own provenance:
    // normals, light, block light and shore fade all interpolate meaningfully across two meshes, which
    //
    // VERTEX-MAJOR. Attribute-major re-read `src`, `srcOwnerIdx` and the provenance array — and
    // re-ran the validity test — once per attribute, four times over for one answer. The stride is a
    // local rather than `a.itemSize` read inside the index arithmetic, and the two sizes are written
    // out: three of the four attributes are itemSize 1, where the inner loop was a bounds check and
    // two multiplies to move a single float.
    const copy: { dst: AttrArray; byOwner: (AttrArray | undefined)[]; wide: boolean }[] = [];
    for (const a of TERRAIN_ATTRS) {
      if (a.name === 'position' || a.name === 'materialIds' || a.name === 'materialWeights') continue;
      // The attribute's own byte format, so the per-vertex copy below moves the source's bytes as-is.
      const dst = allocAttrArray(a, n);
      attrs[a.name] = dst;
      copy.push({ dst, byOwner: srcArrays(a.name), wide: a.itemSize === 3 });
    }
    for (let v = 0; v < n; v++) {
      const src = strip.src[v];
      const oi = strip.srcOwnerIdx[v];
      // A zero-filled attribute is a WRONG value, not a missing one — the shader reads it as no
      // material and no light, i.e. a flat grey patch laid over the terrain, which is worse than
      if (oi < 0 || !(src >= 0)) { return null; }
      for (let ci = 0; ci < copy.length; ci++) {
        const c = copy[ci];
        const from = c.byOwner[oi];
        if (!from) { return null; }
        if (c.wide) {
          const d = v * 3, sb = src * 3;
          c.dst[d] = from[sb]; c.dst[d + 1] = from[sb + 1]; c.dst[d + 2] = from[sb + 2];
        } else {
          c.dst[v] = from[src];
        }
      }
    }

    /** THE MATERIAL PAIR IS PER TRIANGLE, and copying it per vertex was never meaningful. */
    // MATERIAL_SLOT_STRIDE bytes a vertex (vertexFormat.ts): three ids + pad; weights are normalized.
    const ids = new Uint8Array(n * MATERIAL_SLOT_STRIDE);
    const bary = new Uint8Array(n * MATERIAL_SLOT_STRIDE);
    const idsByOwner = srcArrays('materialIds');
    const baryByOwner = srcArrays('materialWeights');
    const corner = [0, 0, 0];
    const mine = [0, 0, 0];
    for (let t = 0; t + 2 < strip.indexCount; t += 3) {
      corner[0] = strip.indices[t];
      corner[1] = strip.indices[t + 1];
      corner[2] = strip.indices[t + 2];
      for (let k = 0; k < 3; k++) {
        const v = corner[k];
        const src = strip.src[v];
        const oi = strip.srcOwnerIdx[v];
        const mi = oi >= 0 ? idsByOwner[oi] : undefined;
        const mw = oi >= 0 ? baryByOwner[oi] : undefined;
        if (!mi || !mw || !(src >= 0)) { return null; }
        // WHICH CORNER THIS SOURCE VERTEX WAS, by argmax rather than an exact 1: a vertex that has
        // been merged or resampled can carry weights that no longer sum to a clean basis vector, and
        let best = 0;
        const sb = src * MATERIAL_SLOT_STRIDE;
        for (let c = 1; c < 3; c++) if (mw[sb + c] > mw[sb + best]) best = c;
        mine[k] = mi[sb + best];
      }
      // Corner k is slot k: its id in its own slot, one-hot weight in the same slot, 0 elsewhere —
      // the encoding the terrain vertices use (MeshGeometry), so recoverMaterialIds reads both alike.
      for (let k = 0; k < 3; k++) {
        const b = corner[k] * MATERIAL_SLOT_STRIDE;
        ids[b + k] = mine[k];
        bary[b + k] = 255;
      }
    }
    attrs.materialIds = ids;
    attrs.materialWeights = bary;
    // NOT a TERRAIN_ATTRS entry: it exists only on aprons, so terrain meshes pay nothing for it and
    // the shader's `skirtKind` reads the WebGL default of 0 on everything else — which is why no
    // seam kind is numbered 0 (see seamField's KIND_FAN).
    attrs.skirtKind = new Float32Array(strip.kind.subarray(0, n));
    // THE USED PREFIX ONLY — the strip's buffers are pooled, so their `length` is capacity. Copies
    // rather than views for the same reason: the next seam overwrites them.
    return {
      positions: strip.positions.slice(0, n * 3),
      indices: strip.indices.slice(0, strip.indexCount),
      attrs,
    };
  }

  /**
   * EVERY chunk is gone at once — a world switch or a clear-and-reload, where the caller drops the
   * geometry map wholesale rather than evicting chunk by chunk.
   *
   * It cannot be expressed as a loop of `onRemoved`: that one RE-QUEUES the departed chunk's
   * neighbourhood so the surviving neighbours re-decide their seams, and here nothing survives — the
   * loop would leave `pending`/`crossPending` full of keys into a world that no longer exists, which
   * is the exact payload this needs to drop.
   *
   * WHY IT HAS TO EXIST AT ALL: chunk keys carry no world identity ("cx,cy,cz,lod" is the same
   * coordinate in every world), so anything left here ALIASES onto the next world. Two of these are
   * actively harmful rather than merely stale — `skirtSig` makes the stitcher conclude a skirt it can
   * no longer see is already correct, so the new world never gets one; and `bands` / `fineBands`
   * hold index-buffer masks for geometry that has been disposed, which a later restore writes into
   * whatever now occupies that key.
   *
   * The strips themselves are NOT retracted here: their sink (TerrainBatch) is being disposed in the
   * same breath, and dropping them one at a time through a batch that is tearing down is both
   * wasted work and an ordering hazard.
   */
  reset(): void {
    this.pending.clear();
    this.crossPending.clear();
    this.flipped.clear();
    this.unresolved.clear();
    this.skirtLayers.clear();
    this.skirtSig.clear();
    this.skirtReads.clear();
    this.readBy.clear();
    this.normalsDirty.clear();
    this.indicesDirty.clear();
    this.flushing = false;
    this.skirtFaceTris.clear();
    this.faceEmpty.clear();
    this.vertexMemo.clear();
    this.bridgedOwners.clear();
    this.finerCache.clear();
    this.requiredByOwner.clear();
    this.regMemo.clear();
    this.drawMemo.clear();
    this.drawMemoP.clear();
    this.coordMemo.clear();
    this.distSqCache.clear();
    this.bands.clear();
    this.diagBands.clear();
    this.diagSig.clear();
    this.fineBands.clear();
    this.fineMaskOwners.clear();
    // The centre is re-supplied by setPlayerCenter before every flush, so it needs no value here —
    // but distSqCache above was keyed against the OLD one, and leaving both in place would let a
    // stale entry be read as current.
    this.playerCx = 0;
    this.playerCy = 0;
    this.playerCz = 0;
  }

  /**
   * A chunk is gone: retract whatever it owns (the geometry those strips describe is being disposed)
   * and re-queue every neighbourhood whose seam ownership its departure changes.
   */
  onRemoved(key: string): void {
    this.dropSkirts(key);
    this.requeueReaders(key);
    this.invalidateFineMasksFor(key);
    this.diagBands.delete(key);
    this.diagSig.delete(key);
    this.unresolved.delete(key);
    // Invalidate caches that depend on this chunk's state. finerCache is POSITIONAL (candidate
    // coordinates across a face — computeFinerNeighbours reads no residency), so a removal changes no
    // entry but the departed owner's own; clearing it whole per eviction (as this once did) made every
    // owner recompute six faces on the next flush, for a batch of 64 evictions at a time.
    this.invalidateFinerCache(key);
    this.invalidateRequiredFor(key);
    const { cx, cy, cz, lod } = this.coordsOf(key);
    // The departed chunk itself rides the scope so the pass can re-derive the OWNERS whose seams
    // it was a participant in — they are not otherwise in scope, and their skirts now bridge to a
    this.crossPending.add(key);
    if (lod > 0) {
      this.enqueueFinerNeighbours(cx, cy, cz, lod);
    } else {
      // For lod-0 chunks, add any coarser owners to crossPending so they can recompute skirts.
      // The fine chunk may have been a participant in their skirts.
      const registered = (k: string) => this.geometries.has(k);
      for (const dir of SEAM_DIRS) {
        const c = coarserNeighbourOf(cx, cy, cz, lod, dir, registered);
        if (c) this.crossPending.add(c.key);
      }
    }
    // Same-LOD neighbours re-decide too — at EVERY lod. The old `lod > 0` guard meant a lod-0
    // eviction notified nobody, and the neighbour whose face just flipped from same-LOD to
    for (const dir of SEAM_DIRS) {
      const nbrKey = chunkKeyLod(cx + dir.dx, cy + dir.dy, cz + dir.dz, lod);
      if (this.geometries.get(nbrKey)?.hasGeometry()) this.pending.add(nbrKey);
    }
  }

  /**
   * Queue every finer chunk that FACES a coarse one for a cross-LOD re-evaluation — the participants
   * of whatever skirt that coarse chunk owes, which have to be re-read when its own state moves.
   */
  private enqueueFinerNeighbours(cx: number, cy: number, cz: number, lod: number): void {
    const base = SCRATCH_BASE;
    const p = SCRATCH_POS;
    let open = 0; // bitmask of directions with undrawn volume across them
    for (let fineLod = lod - 1; fineLod >= Math.max(0, lod - 2); fineLod--) {
      const n = 1 << (lod - fineLod); // fine chunks spanning this one, per axis
      base[0] = cx * n; base[1] = cy * n; base[2] = cz * n;
      const first = fineLod === lod - 1;
      let nextOpen = 0;
      for (let d = 0; d < SEAM_DIRS.length; d++) {
        if (!first && (open & (1 << d)) === 0) continue;
        const dir = SEAM_DIRS[d];
        const axis = dir.thisFace >> 1;
        const a = (axis + 1) % 3, b = (axis + 2) % 3;
        p[axis] = dir.positive ? base[axis] + n : base[axis] - 1;
        for (let i = 0; i < n; i++) {
          for (let j = 0; j < n; j++) {
            p[a] = base[a] + i;
            p[b] = base[b] + j;
            const key = chunkKeyLod(p[0], p[1], p[2], fineLod);
            if (this.geometries.get(key)?.hasGeometry()) {
              this.crossPending.add(key);
              if (!this.isDisplayed(key)) nextOpen |= 1 << d;
            } else {
              nextOpen |= 1 << d; // nothing here: whatever is finer looks straight past this level
            }
          }
        }
      }
      if (nextOpen === 0) return;
      open = nextOpen;
    }
  }


  /** CUMULATIVE, PER LAYER — why a cross-LOD face did or did not get a strip, over the session. */





  /** Reconcile one seam between two chunk geometries across all layers. */
  private reconcileSeam(
    thisGeo: ChunkGeometry,
    thisFace: number,
    nbrGeo: ChunkGeometry,
    nbrFace: number,
    average: boolean,
    lod: number = 0,
  ): { thisChanged: boolean; nbrChanged: boolean } {
    const axis = thisFace >> 1;        // 0=X, 1=Y, 2=Z
    const a = (axis + 1) % 3;          // first in-plane axis
    const b = (axis + 2) % 3;          // second in-plane axis
    // World-unit positions carry the LOD scale baked in — quantize in the chunk's own grid units.
    const gridUnit = VOXEL_SCALE * (1 << lod);
    let anyThis = false;
    let anyNbr = false;

    for (let layer = 0; layer < LAYER_COUNT; layer++) {
      const tB = thisGeo.getBoundary(layer);
      const nB = nbrGeo.getBoundary(layer);
      if (!tB || !nB) continue;

      const tStart = tB.faceOffsets[thisFace], tEnd = tB.faceOffsets[thisFace + 1];
      const nStart = nB.faceOffsets[nbrFace], nEnd = nB.faceOffsets[nbrFace + 1];
      if (tStart === tEnd || nStart === nEnd) continue;

      const tGeo = thisGeo.getLayerGeometry(layer);
      const nGeo = nbrGeo.getLayerGeometry(layer);
      if (!tGeo || !nGeo) continue;

      const tPos = tGeo.getAttribute('position').array as Float32Array;
      const tNrm = tGeo.getAttribute('normal').array as Float32Array;
      const nPos = nGeo.getAttribute('position').array as Float32Array;
      const nNrm = nGeo.getAttribute('normal').array as Float32Array;

      // Group both sides' boundary-face vertices by in-plane grid position.
      const T = this.groupFace(0, tB.indices, tStart, tEnd, tPos, a, b, gridUnit);
      const N = this.groupFace(1, nB.indices, nStart, nEnd, nPos, a, b, gridUnit);

      let touchedThis = false;
      let touchedNbr = false;

      for (let ts = 0; ts < T.distinct; ts++) {
        const ns = N.slotOf.get(T.keys[ts]);
        if (ns === undefined) continue;

        const t0 = T.vert[T.head[ts]] * 3;
        const n0 = N.vert[N.head[ns]] * 3;

        if (average) {
          let x = tNrm[t0] + nNrm[n0];
          let y = tNrm[t0 + 1] + nNrm[n0 + 1];
          let z = tNrm[t0 + 2] + nNrm[n0 + 2];
          const len = Math.hypot(x, y, z);
          if (len > 1e-6) { x /= len; y /= len; z /= len; }
          else { x = tNrm[t0]; y = tNrm[t0 + 1]; z = tNrm[t0 + 2]; }
          // Write only the side(s) whose normal actually moved — skip no-op rewrites
          // of an already-settled seam so its group isn't needlessly re-merged.
          if (differs(tNrm, t0, x, y, z)) { writeNormal(tNrm, T, ts, x, y, z); touchedThis = true; }
          if (differs(nNrm, n0, x, y, z)) { writeNormal(nNrm, N, ns, x, y, z); touchedNbr = true; }
        } else {
          // Copy this chunk's normal into the stale neighbor's duplicates (if different).
          const x = tNrm[t0], y = tNrm[t0 + 1], z = tNrm[t0 + 2];
          if (differs(nNrm, n0, x, y, z)) { writeNormal(nNrm, N, ns, x, y, z); touchedNbr = true; }
        }
      }

      if (touchedThis) { thisGeo.markNormalsNeedUpdate(layer); anyThis = true; }
      if (touchedNbr) { nbrGeo.markNormalsNeedUpdate(layer); anyNbr = true; }
    }

    return { thisChanged: anyThis, nbrChanged: anyNbr };
  }

  /** One boundary face's vertices grouped by in-plane grid position, into pool slot `slot`. */
  private groupFace(
    slot: number,
    faceIndices: Uint32Array,
    start: number,
    end: number,
    positions: Float32Array,
    a: number,
    b: number,
    gridUnit: number = VOXEL_SCALE,
  ): FaceGroups {
    const g = faceGroups(slot, end - start);
    for (let i = start; i < end; i++) {
      const vi = faceIndices[i];
      const base = vi * 3;
      const qa = Math.round((positions[base + a] / gridUnit) * QUANT);
      const qb = Math.round((positions[base + b] / gridUnit) * QUANT);
      const key = packKey(qa, qb);
      const e = i - start;
      g.vert[e] = vi;
      g.next[e] = -1;
      const at = g.slotOf.get(key);
      if (at === undefined) {
        const ns = g.distinct++;
        g.slotOf.set(key, ns);
        g.keys[ns] = key;
        g.head[ns] = e;
        g.tail[ns] = e;
      } else {
        g.next[g.tail[at]] = e;
        g.tail[at] = e;
      }
    }
    return g;
  }
}







/** IS THIS PARTICIPANT'S MESH STILL COMING? — the only condition under which a skirt is a debt. */
function meshInFlight(geo: ChunkGeometry | undefined): boolean {
  return !!geo && !geo.hasGeometry() && (geo.meshGeneration ?? 0) === 0;
}

/** Scratch coordinate triples for the cross-LOD pass (single-threaded, non-reentrant). */
const SCRATCH_BASE = [0, 0, 0];
const SCRATCH_POS = [0, 0, 0];
/** The owner's 26 own-lod neighbours, in a fixed order so the signature is positional. */
function flankKeys(o: { cx: number; cy: number; cz: number; lod: number }): string[] {
  const out: string[] = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dy === 0 && dz === 0) continue;
        out.push(chunkKeyLod(o.cx + dx, o.cy + dy, o.cz + dz, o.lod));
      }
    }
  }
  return out;
}

const SCRATCH_CELL = [0, 0, 0];
const SCRATCH_VOXEL = [0, 0, 0];
const SCRATCH_TRACE = [0, 0, 0];
const SCRATCH_NBR = [0, 0, 0];

/** A chunk's voxel data with its + neighbours' (or the margin policy's) resolved lazily, per face. */
interface GridSampler {
  cx: number; cy: number; cz: number; lod: number;
  data: Uint32Array | null;
  /** Indexed `ox | oy << 1 | oz << 2`; undefined = not yet resolved, null = policy 'unknown'. */
  nbr: (Uint32Array | null | undefined)[];
}

/**
 * The NEAREST COARSER ANCESTOR that is actually drawing the volume across `dir` — the chunk this
 * face has to agree with — or null when nothing coarser owns those pixels in a bridgeable way.
 */
function coarserNeighbourOf(
  cx: number, cy: number, cz: number, lod: number, dir: SeamDir,
  drawableAt: (key: string) => boolean,
): { key: string; cx: number; cy: number; cz: number; lod: number } | null {
  const nx = cx + dir.dx, ny = cy + dir.dy, nz = cz + dir.dz;
  const along = dir.dx !== 0 ? nx : dir.dy !== 0 ? ny : nz;
  const r = dir.positive ? along : along + 1;
  let px = nx, py = ny, pz = nz;
  for (let k = 1; lod + k <= MAX_PACKABLE_LOD; k++) {
    px >>= 1; py >>= 1; pz >>= 1;
    const step = 1 << k;
    if ((((r % step) + step) % step) !== 0) return null; // and nothing coarser can be aligned
    const key = chunkKeyLod(px, py, pz, lod + k);
    // NEAREST wins: two levels cannot both own the volume, and the finer one is the surface
    // actually abutting this face — bridging past it would span a boundary that is not there.
    if (drawableAt(key)) return { key, cx: px, cy: py, cz: pz, lod: lod + k };
  }
  return null;
}


/** Normals within this per-component distance are treated as unchanged. */
const NORMAL_EPS = 1e-4;

/** True if the normal at `base` differs from (x,y,z) beyond NORMAL_EPS on any axis. */
function differs(normals: Float32Array, base: number, x: number, y: number, z: number): boolean {
  return Math.abs(normals[base] - x) > NORMAL_EPS
    || Math.abs(normals[base + 1] - y) > NORMAL_EPS
    || Math.abs(normals[base + 2] - z) > NORMAL_EPS;
}

/** Write a normal to every expanded vertex sharing one grouped position. */
function writeNormal(
  normals: Float32Array, g: FaceGroups, slot: number, x: number, y: number, z: number,
): void {
  for (let e = g.head[slot]; e >= 0; e = g.next[e]) {
    const o = g.vert[e] * 3;
    normals[o] = x;
    normals[o + 1] = y;
    normals[o + 2] = z;
  }
}
